import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  Tab,
  Tabs,
  Chip,
  Tooltip,
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  LinearProgress,
  CircularProgress,
  Snackbar,
  Portal,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { styled, keyframes } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReplayIcon from "@mui/icons-material/Replay";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { TASKS_API_BASE } from "./apiConfig";
import {
  IGNORED_FOR_VERDICT,
  LIA_DIRECT_REPAIR_TESTS,
  OPENAI_HTML_REPAIR_TEST,
  QA_METADATA_KEYS,
  TRUE_MEANS_FAIL,
  buildLiaRepairPlan,
  extractQaRetestForSku,
  getFailedQaTestNames,
  getSkuStatus,
  isEntryFailed,
  shouldSkipSizeTestForSku,
} from "./productQaBulkRepair";

import "./App.css";

const isLocalQaFrontend =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);
const QA_TASKS_API_BASE = isLocalQaFrontend
  ? "http://localhost:5888/api/tasks"
  : TASKS_API_BASE;
const API_BASE = `${QA_TASKS_API_BASE}/testProducts`;
const PRODUCT_QA_JOB_STORAGE_KEY = "productQaRepairJobId";
const PRODUCT_QA_JOB_NOTIFIED_KEY = "productQaRepairJobLastNotified";
const ACTIVE_PRODUCT_QA_JOB_STATUSES = new Set(["QUEUED", "RUNNING"]);
const OPENAI_HTML_REPAIR_TIMEOUT_MS = 540000;

const getProductQaJobHeaders = (headers = {}) => {
  const token =
    typeof window !== "undefined"
      ? window.localStorage.getItem("authToken") ||
        window.sessionStorage.getItem("authToken")
      : null;
  return {
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const readApiPayload = async (response) => {
  const raw = await response.text();
  if (!raw) return {};

  try {
    return JSON.parse(raw);
  } catch (_) {
    return {
      details: raw
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 500),
    };
  }
};

const getApiErrorMessage = (response, payload = {}) => {
  const detail = payload.details || payload.error;
  if (detail) {
    return typeof detail === "string" ? detail : JSON.stringify(detail);
  }
  return `[${response.status}] ${response.statusText} — ${response.url}`;
};

const isConfirmedHtmlQa = (qa) =>
  qa?.passed === true &&
  qa.skipped !== true &&
  !qa.error &&
  !(Array.isArray(qa.errors) ? qa.errors.length : qa.errors);

const isAiRepairConfirmed = (repair) =>
  repair?.applied?.ok === true && repair.validationConfirmed !== false;

const htmlRepairDiagnostics = (result = {}) => {
  const asText = (value) =>
    typeof value === "string" ? value.trim() : value ? JSON.stringify(value) : "";
  const qa = result.afterQa || result.beforeQa;
  const skipped = Array.isArray(result.skipped) ? result.skipped : [];
  return Array.from(new Set([
    result.reason === "ALREADY_VALID" ? "" : result.reason,
    result.details,
    ...skipped.flatMap((item) => typeof item === "string" ? [item] : [
      item?.reason, item?.details,
      ...(Array.isArray(item?.safetyReasons) ? item.safetyReasons : []),
    ]),
    ...(Array.isArray(result.safety?.reasons) ? result.safety.reasons : []),
    ...(qa && !isConfirmedHtmlQa(qa) ? [typeof qa === "string" ? qa : "", qa.summary, qa.error, qa.errors,
      ...(Array.isArray(qa.issues) ? qa.issues.map((issue) => issue?.problem) : []),
    ] : []),
  ].map(asText).filter(Boolean))).join(" — ");
};

function Page4() {
  const [jsonData, setJsonData] = useState({});
  const [activeTab, setActiveTab] = useState("all");
  const [selectedSku, setSelectedSku] = useState("");
  const [failedCount, setFailedCount] = useState(0);
  const [passedCount, setPassedCount] = useState(0);
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [retesting, setRetesting] = useState({}); // { [sku]: boolean }
  const [searchingSku, setSearchingSku] = useState(false);

  // --- AI Repairs state ---
  const [aiRepairs, setAiRepairs] = useState({});
  const [repairing, setRepairing] = useState({}); // { [sku]: boolean }
  const [repairErr, setRepairErr] = useState({}); // { [sku]: string }
  const [repairingTest, setRepairingTest] = useState({}); // { [`${sku}:${test}`]: boolean }
  const [repairErrTest, setRepairErrTest] = useState({}); // { [`${sku}:${test}`]: string }
  const [repairStatusTest, setRepairStatusTest] = useState({}); // { [`${sku}:${test}`]: "success" | "warning" | "error" }
  const [repairFeedbackTest, setRepairFeedbackTest] = useState({});
  const [bulkRepairing, setBulkRepairing] = useState(false);
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkProgress, setBulkProgress] = useState({
    completed: 0,
    total: 0,
    sku: "",
    changed: 0,
    passed: 0,
    manual: 0,
    errors: 0,
    manualSkus: [],
    errorSkus: [],
  });
  const bulkPollNowRef = useRef(null);
  const bulkRefreshStateRef = useRef(new Map());
  const terminalBulkJobsLoadedRef = useRef(new Set());

  // Notifications
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState("");
  const [notifySeverity, setNotifySeverity] = useState("success");

  const getTestKeyVariants = (testName) => {
    const key = String(testName || "");
    return Array.from(
      new Set([key, key.replace(/ /g, "_"), key.replace(/_/g, " ")]),
    );
  };

  const clearSkippedStateForTest = (
    tests = {},
    testName,
    sku,
    { removeFailedTest = true } = {},
  ) => {
    const variants = getTestKeyVariants(testName);
    const updated = { ...tests };

    const skippedTests = Array.isArray(updated.SKIPPED_TESTS)
      ? updated.SKIPPED_TESTS.filter((name) => !variants.includes(name))
      : [];
    if (skippedTests.length) {
      updated.SKIPPED_TESTS = skippedTests;
    } else {
      delete updated.SKIPPED_TESTS;
    }

    variants.forEach((variant) => {
      if (!(variant in updated)) return;
      const value = updated[variant];
      if (value === "SKIPPED") {
        delete updated[variant];
      } else if (value && typeof value === "object" && value.skipped === true) {
        if (Object.prototype.hasOwnProperty.call(value, "skippedOriginal")) {
          if (value.skippedOriginal === undefined) {
            delete updated[variant];
          } else {
            updated[variant] = value.skippedOriginal;
          }
          return;
        }
        const { skipped, ...rest } = value;
        updated[variant] = rest;
      }
    });

    if (removeFailedTest) {
      updated.failedTests = Array.isArray(updated.failedTests)
        ? updated.failedTests.filter((name) => !variants.includes(name))
        : [];
    }
    updated.verdict = getSkuStatus(updated, sku);
    return updated;
  };

  useEffect(() => {
    fetchTestResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Progress bar total ---
  const totalSkus = failedCount + passedCount;
  const hasSingleQaActionInProgress =
    searchingSku ||
    Object.values(retesting).some(Boolean) ||
    Object.values(repairing).some(Boolean) ||
    Object.values(repairingTest).some(Boolean);

  useEffect(() => {
    if (activeTab === "skipped") {
      setActiveTab("all");
    }
  }, [activeTab]);

  const buildRetestedSkuState = (current = {}, incoming = {}, sku = "") => {
    const next = { ...(incoming || {}) };
    ["date_created", "ID", "slug", "permalink", "SKIPPED_TESTS"].forEach(
      (key) => {
        if (!(key in next) && current[key] !== undefined) {
          next[key] = current[key];
        }
      },
    );
    next.verdict = getSkuStatus(next, sku);
    return next;
  };

  const recalcTotals = (dataObj) => {
    let failed = 0,
      passed = 0;
    Object.keys(dataObj).forEach((sku) => {
      const status = getSkuStatus(dataObj[sku] || {}, sku);
      console.log("[recalcTotals] SKU", sku, "=>", status);
      if (status === "FAILED") failed++;
      else passed++;
    });
    setFailedCount(failed);
    setPassedCount(passed);
  };

  const retestSku = async (sku, options = {}) => {
    try {
      setRetesting((prev) => ({ ...prev, [sku]: true }));
      const response = await fetch(
        `${API_BASE}/verify-products?sku=${encodeURIComponent(sku)}&openaiHtml=1`,
        {
          method: "POST",
          headers: getProductQaJobHeaders({
            "Content-Type": "application/json",
          }),
        },
      );
      const data = await response.json();
      if (!data || typeof data !== "object") {
        console.error("Invalid single-SKU response", data);
        return;
      }
      const incoming = data[sku] || data[Object.keys(data)[0]];
      if (!incoming) return;

      let retestedSkuState = null;
      setJsonData((prev) => {
        let nextSkuTests = buildRetestedSkuState(
          prev[sku] || {},
          incoming,
          sku,
        );
        if (options.unskippedTestName) {
          nextSkuTests = clearSkippedStateForTest(
            nextSkuTests,
            options.unskippedTestName,
            sku,
            { removeFailedTest: false },
          );
        }
        nextSkuTests.verdict = getSkuStatus(nextSkuTests, sku);
        retestedSkuState = nextSkuTests;
        const updated = { ...prev, [sku]: nextSkuTests };
        recalcTotals(updated);
        return updated;
      });
      if (retestedSkuState) {
        await saveTestResult(sku, retestedSkuState);
      }
    } catch (e) {
      console.error("Re-test failed for", sku, e);
    } finally {
      setRetesting((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const normalizeSkuQuery = (value) =>
    String(value || "")
      .trim()
      .toUpperCase();

  const isExactSkuQuery = (value) =>
    /^[A-Z]{2,8}LEB\d{5,}$/i.test(String(value || "").trim());

  const loadSearchedSku = async (sku) => {
    const normalizedSku = normalizeSkuQuery(sku);
    if (!normalizedSku) return;

    try {
      setSearchingSku(true);
      setRetesting((prev) => ({ ...prev, [normalizedSku]: true }));

      const response = await fetch(
        `${API_BASE}/verify-products?sku=${encodeURIComponent(normalizedSku)}&openaiHtml=1`,
        {
          method: "POST",
          headers: getProductQaJobHeaders({
            "Content-Type": "application/json",
          }),
          body: JSON.stringify({ sku: normalizedSku }),
        },
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.details ||
            data?.error ||
            `[${response.status}] ${response.statusText}`,
        );
      }

      const responseSku = data[normalizedSku]
        ? normalizedSku
        : Object.keys(data)[0];
      const incoming = responseSku ? data[responseSku] : null;
      if (!responseSku || !incoming) {
        throw new Error("SKU-ul nu a întors rezultate QA.");
      }

      let mergedForStatus = null;
      setJsonData((prev) => {
        mergedForStatus = buildRetestedSkuState(
          prev[responseSku] || {},
          incoming,
          responseSku,
        );
        const updated = { ...prev, [responseSku]: mergedForStatus };
        recalcTotals(updated);
        return updated;
      });

      const status = getSkuStatus(mergedForStatus || incoming, responseSku);
      setQuery(responseSku);
      setSelectedSku(responseSku);
      setActiveTab(status.toLowerCase());
      setNotifySeverity(status === "FAILED" ? "warning" : "success");
      setNotifyMsg(
        `SKU ${responseSku} a fost rulat și adăugat în lista ${status}.`,
      );
      setNotifyOpen(true);
    } catch (error) {
      console.error("Exact SKU search failed:", error);
      setNotifySeverity("error");
      setNotifyMsg(
        `Nu am putut rula SKU-ul ${normalizedSku}: ${error.message || "eroare necunoscută"}`,
      );
      setNotifyOpen(true);
    } finally {
      setSearchingSku(false);
      setRetesting((prev) => ({ ...prev, [normalizedSku]: false }));
    }
  };

  const fetchTestResults = async ({ force = false } = {}) => {
    try {
      const response = await fetch(`${API_BASE}/get-test-results`);
      const results = await response.json();
      const formattedResults = results.reduce((acc, result) => {
        acc[result.sku] = JSON.parse(result.test_cases);
        return acc;
      }, {});
      setTestResults(formattedResults);
      setJsonData((prev) => {
        if (!force && Object.keys(prev).length) return prev;
        recalcTotals(formattedResults);
        return formattedResults;
      });
      return true;
    } catch (error) {
      console.error("Error fetching test results:", error);
      return false;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const runTests = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/verify-products`, {
        method: "POST",
        headers: getProductQaJobHeaders({
          "Content-Type": "application/json",
        }),
      });

      const data = await response.json();
      if (data && typeof data === "object") {
        let combinedData = {};
        Object.keys(data).forEach((sku) => {
          // Put old cached results first, then NEW backend data to ensure new keys persist
          combinedData[sku] = { ...(testResults[sku] || {}), ...data[sku] };
        });

        setJsonData(combinedData);

        let failed = 0,
          passed = 0;
        Object.keys(combinedData).forEach((sku) => {
          const status = getSkuStatus(combinedData[sku] || {}, sku);
          console.log("[runTests] SKU", sku, "=>", status);
          if (status === "FAILED") failed++;
          else passed++;
        });
        setFailedCount(failed);
        setPassedCount(passed);
      } else {
        console.error("Invalid data format:", data);
      }
    } catch (error) {
      console.error("Error running tests:", error);
    } finally {
      setLoading(false);
    }
  };

  const requestApiPayload = async (url, options = {}) => {
    const {
      timeoutMs = 180000,
      signal: externalSignal,
      ...fetchOptions
    } = options;
    const controller = new AbortController();
    let timedOut = false;
    const abortFromExternalSignal = () => controller.abort();
    if (externalSignal?.aborted) {
      controller.abort();
    } else if (externalSignal) {
      externalSignal.addEventListener("abort", abortFromExternalSignal, {
        once: true,
      });
    }
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      });
      const payload = await readApiPayload(response);
      if (!response.ok) {
        const error = new Error(getApiErrorMessage(response, payload));
        error.status = response.status;
        error.payload = payload;
        throw error;
      }
      return payload;
    } catch (error) {
      if (error?.name === "AbortError") {
        const abortError = new Error(
          timedOut
            ? `Request timeout după ${Math.ceil(timeoutMs / 1000)} secunde.`
            : "Operațiunea a fost anulată.",
        );
        abortError.code = timedOut ? "REQUEST_TIMEOUT" : "REQUEST_ABORTED";
        throw abortError;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener("abort", abortFromExternalSignal);
    }
  };

  const applyBulkRepairJobState = (job) => {
    if (!job) return;
    const results = Array.isArray(job.results) ? job.results : [];
    const errorSkus = results
      .filter((item) => item?.status === "ERROR")
      .map((item) => item.sku)
      .filter(Boolean);
    const manualSkus = results
      .filter((item) => item?.status === "MANUAL")
      .map((item) => item.sku)
      .filter(Boolean);
    const isActive = ACTIVE_PRODUCT_QA_JOB_STATUSES.has(job.status);

    setBulkJob(job);
    setBulkRepairing(isActive);
    setBulkProgress({
      completed: Number(job.completed || 0),
      total: Number(job.total || 0),
      sku: job.currentSku || "",
      changed: Number(job.changed || 0),
      passed: Number(job.passed || 0),
      manual: Number(job.manual || 0),
      errors: Number(job.errors || 0),
      manualSkus,
      errorSkus,
    });

    if (typeof window !== "undefined" && job.id) {
      window.localStorage.setItem(PRODUCT_QA_JOB_STORAGE_KEY, job.id);
    }

    const finished =
      !isActive &&
      ["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED"].includes(job.status);
    if (!finished || typeof window === "undefined") return;

    if (!bulkRefreshStateRef.current.has(job.id)) {
      bulkRefreshStateRef.current.set(job.id, "pending");
      fetchTestResults({ force: true }).then((refreshed) => {
        if (refreshed) {
          bulkRefreshStateRef.current.set(job.id, "completed");
          terminalBulkJobsLoadedRef.current.add(job.id);
        } else {
          bulkRefreshStateRef.current.delete(job.id);
        }
      });
    }

    const lastNotified = window.localStorage.getItem(
      PRODUCT_QA_JOB_NOTIFIED_KEY,
    );
    if (lastNotified === job.id) return;
    window.localStorage.setItem(PRODUCT_QA_JOB_NOTIFIED_KEY, job.id);
    setNotifySeverity(
      job.status === "COMPLETED" && job.emailStatus === "SENT"
        ? "success"
        : "warning",
    );
    const emailSummary =
      job.emailStatus === "SENT"
        ? "Raportul a fost trimis pe suport@luxuraelite.ro."
        : job.emailStatus === "FAILED"
          ? "Emailul nu a putut fi trimis."
          : "Trimiterea raportului email nu a fost confirmată.";
    setNotifyMsg(
      `Repară LIA global s-a încheiat: ${job.passed || 0} PASSED, ${job.manual || 0} manual, ${job.errors || 0} erori. ${emailSummary}`,
    );
    setNotifyOpen(true);
  };

  useEffect(() => {
    let stopped = false;
    let timerId = null;
    let polling = false;

    const pollBulkRepairJob = async () => {
      if (stopped || polling) return;
      polling = true;
      let nextDelay = 15000;
      try {
        const requestOptions = {
          headers: getProductQaJobHeaders(),
          timeoutMs: 30000,
        };
        const activePayload = await requestApiPayload(
          `${API_BASE}/repair-lia-jobs/active`,
          requestOptions,
        );
        let job = activePayload?.job || null;

        if (!job && typeof window !== "undefined") {
          const storedJobId = window.localStorage.getItem(
            PRODUCT_QA_JOB_STORAGE_KEY,
          );
          if (
            storedJobId &&
            !terminalBulkJobsLoadedRef.current.has(storedJobId)
          ) {
            try {
              const storedPayload = await requestApiPayload(
                `${API_BASE}/repair-lia-jobs/${encodeURIComponent(storedJobId)}`,
                requestOptions,
              );
              job = storedPayload?.job || null;
            } catch (error) {
              if (error.status === 404) {
                window.localStorage.removeItem(PRODUCT_QA_JOB_STORAGE_KEY);
              } else {
                throw error;
              }
            }
          }
        }

        if (job && !stopped) {
          applyBulkRepairJobState(job);
          nextDelay = ACTIVE_PRODUCT_QA_JOB_STATUSES.has(job.status)
            ? 3000
            : 15000;
        }
      } catch (error) {
        console.error("Product QA repair job polling failed:", error);
        nextDelay = 15000;
      } finally {
        polling = false;
        if (!stopped) {
          timerId = window.setTimeout(pollBulkRepairJob, nextDelay);
        }
      }
    };

    bulkPollNowRef.current = () => {
      if (timerId) window.clearTimeout(timerId);
      timerId = null;
      pollBulkRepairJob();
    };
    pollBulkRepairJob();

    return () => {
      stopped = true;
      if (timerId) window.clearTimeout(timerId);
      bulkPollNowRef.current = null;
    };
    // Polling is intentionally tied only to the page mount. The server job
    // continues independently after this component unmounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestFreshSkuTests = async (
    sku,
    currentTests = {},
    { signal, requireHtmlResult = false } = {},
  ) => {
    let lastError = null;
    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        const payload = await requestApiPayload(
          `${API_BASE}/verify-products?sku=${encodeURIComponent(sku)}&openaiHtml=1`,
          {
            method: "POST",
            headers: getProductQaJobHeaders({
              "Content-Type": "application/json",
            }),
            body: JSON.stringify({ sku, openaiHtml: true }),
            signal,
            timeoutMs: 150000,
          },
        );
        const incoming = requireHtmlResult
          ? payload?.[sku]
          : extractQaRetestForSku(payload, sku);
        if (requireHtmlResult) {
          const value = incoming?.[OPENAI_HTML_REPAIR_TEST];
          const skipped = getTestKeyVariants(OPENAI_HTML_REPAIR_TEST)
            .some((variant) => incoming?.SKIPPED_TESTS?.includes(variant));
          const hasQaResult = typeof value === "boolean" ||
            (value && typeof value === "object" && typeof value.passed === "boolean" &&
              value.skipped !== true && !value.error &&
              !(Array.isArray(value.errors) ? value.errors.length : value.errors));
          if (!hasQaResult || skipped) {
            const error = new Error(
              `Retestul QA nu a confirmat verificarea HTML pentru SKU-ul cerut. ${
                htmlRepairDiagnostics({ afterQa: value }) ||
                (value?.skipped === true || skipped ? "Verificarea a fost omisă." : "Rezultatul HTML lipsește sau este invalid.")
              }`,
            );
            error.status = 422;
            error.qaUnconfirmed = true;
            throw error;
          }
        }
        if (!incoming) {
          throw new Error(
            "Retestul QA nu a întors un rezultat valid pentru SKU.",
          );
        }
        return buildRetestedSkuState(currentTests, incoming, sku);
      } catch (error) {
        lastError = error;
        if (signal?.aborted) throw error;
        const transient =
          !error.status || error.status === 429 || error.status >= 500;
        if (!transient || attempt === 2) throw error;
        await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
      }
    }
    throw lastError;
  };

  const executeAutomaticLiaRepair = async (
    sku,
    cachedTests = {},
    { signal } = {},
  ) => {
    const applied = [];
    const skipped = [];
    const warnings = [];
    const errors = [];
    const attemptedTests = new Set();
    let changed = false;
    let currentTests = null;

    const isUncertainMutationError = (error) => {
      const details = [
        error?.message,
        typeof error?.payload === "string"
          ? error.payload
          : JSON.stringify(error?.payload || {}),
      ].join(" ");
      return (
        error?.code === "REQUEST_TIMEOUT" ||
        error?.code === "REQUEST_ABORTED" ||
        !error?.status ||
        error.status >= 500 ||
        /timeout|econnreset|socket hang up|failed to fetch|network error/i.test(
          details,
        )
      );
    };

    const throwUncertainWrite = (message) => {
      const error = new Error(message);
      error.code = "UNCERTAIN_WRITE";
      throw error;
    };

    try {
      currentTests = await requestFreshSkuTests(sku, cachedTests, { signal });
    } catch (error) {
      if (signal?.aborted) throw error;
      return {
        ok: false,
        tests: null,
        changed: false,
        applied,
        skipped,
        warnings,
        errors: [
          `QA înainte de reparare: ${error.message || "eroare necunoscută"}`,
        ],
        persisted: false,
        manualTests: getFailedQaTestNames(cachedTests, sku),
      };
    }

    const runDirectRepairGroup = async (failedKeys, label, testsForRequest) => {
      if (!failedKeys.length) return;
      try {
        const result = await requestApiPayload(
          `${API_BASE}/repair-lia-direct`,
          {
            method: "POST",
            headers: getProductQaJobHeaders({
              "Content-Type": "application/json",
            }),
            body: JSON.stringify({
              sku,
              tests: testsForRequest,
              failedKeys,
              retest: false,
            }),
            signal,
            timeoutMs: 90000,
          },
        );
        changed = changed || result.updated === true;
        applied.push(
          ...(Array.isArray(result.applied)
            ? result.applied.map((item) => ({ ...item, repairGroup: label }))
            : []),
        );
        skipped.push(
          ...(Array.isArray(result.skipped)
            ? result.skipped.map((item) => ({ ...item, repairGroup: label }))
            : []),
        );
        if (!result.updated && result.reason) {
          warnings.push(`${label}: ${result.reason}`);
        }
      } catch (error) {
        if (signal?.aborted) throw error;
        const message = `${label}: ${error.message || "eroare necunoscută"}`;
        if (error.status === 409) warnings.push(message);
        else if (isUncertainMutationError(error)) {
          errors.push(`${message} — write-ul Woo nu poate fi confirmat.`);
          throwUncertainWrite(message);
        } else errors.push(message);
      }
    };

    const runHtmlRepair = async () => {
      try {
        const result = await requestApiPayload(
          `${API_BASE}/repair-openai-html`,
          {
            method: "POST",
            headers: getProductQaJobHeaders({
              "Content-Type": "application/json",
            }),
            body: JSON.stringify({ sku, retest: false }),
            signal,
            timeoutMs: OPENAI_HTML_REPAIR_TIMEOUT_MS,
          },
        );
        changed = changed || result.updated === true;
        applied.push(
          ...(Array.isArray(result.applied)
            ? result.applied.map((item) => ({ ...item, repairGroup: "HTML" }))
            : []),
        );
        skipped.push(
          ...(Array.isArray(result.skipped)
            ? result.skipped.map((item) => ({ ...item, repairGroup: "HTML" }))
            : []),
        );
        if (!result.updated && result.reason) {
          warnings.push(`HTML: ${result.reason}`);
        }
      } catch (error) {
        if (signal?.aborted) throw error;
        const message = `HTML: ${error.message || "eroare necunoscută"}`;
        if (error.status === 409) warnings.push(message);
        else if (isUncertainMutationError(error)) {
          errors.push(`${message} — write-ul Woo nu poate fi confirmat.`);
          throwUncertainWrite(message);
        } else errors.push(message);
      }
    };

    // Re-plan from live QA after every pass. Each test is attempted at most
    // once, while newly exposed safe failures can still be repaired.
    for (let pass = 1; pass <= 3; pass += 1) {
      const plan = buildLiaRepairPlan(getFailedQaTestNames(currentTests, sku));
      const pendingGroups = plan.directGroups
        .map((group) => ({
          ...group,
          failedKeys: group.failedKeys.filter(
            (testName) => !attemptedTests.has(testName),
          ),
        }))
        .filter((group) => group.failedKeys.length > 0);
      const shouldRepairHtml =
        plan.repairHtml && !attemptedTests.has(OPENAI_HTML_REPAIR_TEST);

      if (!pendingGroups.length && !shouldRepairHtml) break;

      try {
        for (const group of pendingGroups) {
          group.failedKeys.forEach((testName) => attemptedTests.add(testName));
          await runDirectRepairGroup(
            group.failedKeys,
            group.label,
            currentTests,
          );
        }
        if (shouldRepairHtml) {
          attemptedTests.add(OPENAI_HTML_REPAIR_TEST);
          await runHtmlRepair();
        }

        // Verify only after every attempted write returned a definitive response.
        // Ambiguous writes (timeout/network/5xx) stop this SKU before this point.
        currentTests = await requestFreshSkuTests(sku, currentTests, {
          signal,
        });
      } catch (error) {
        if (signal?.aborted) throw error;
        if (error?.code !== "UNCERTAIN_WRITE") {
          errors.push(`Retest QA: ${error.message || "eroare necunoscută"}`);
        }
        return {
          ok: false,
          tests: null,
          changed,
          applied,
          skipped,
          warnings,
          errors,
          persisted: false,
          manualTests: getFailedQaTestNames(currentTests, sku),
        };
      }

      if (getSkuStatus(currentTests, sku) === "PASSED") break;
    }

    let persisted = false;
    try {
      await saveTestResult(sku, currentTests, { signal });
      persisted = true;
    } catch (error) {
      if (signal?.aborted) throw error;
      errors.push(`Salvare QA: ${error.message || "eroare necunoscută"}`);
    }

    return {
      ok: errors.length === 0,
      tests: currentTests,
      changed,
      applied,
      skipped,
      warnings,
      errors,
      persisted,
      manualTests: getFailedQaTestNames(currentTests, sku),
    };
  };

  const repairAllFailedWithLIA = async () => {
    try {
      if (bulkRepairing || loading || hasSingleQaActionInProgress) return;
      setBulkRepairing(true);
      const visibleFailedSkus = Object.entries(jsonData)
        .filter(([sku, tests]) => getSkuStatus(tests || {}, sku) === "FAILED")
        .map(([sku]) => sku);
      const payload = await requestApiPayload(`${API_BASE}/repair-lia-jobs`, {
        method: "POST",
        headers: getProductQaJobHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          scope: "stored_failed",
          skus: visibleFailedSkus,
        }),
        timeoutMs: 30000,
      });
      if (!payload?.job) {
        throw new Error("Serverul nu a întors jobul Repară LIA.");
      }

      applyBulkRepairJobState(payload.job);
      setNotifySeverity("info");
      setNotifyMsg(
        payload.started
          ? `Repară LIA a pornit pentru ${payload.job.total || 0} SKU-uri. Poți părăsi pagina; vei primi email pe suport@luxuraelite.ro la final.`
          : "Există deja un job Repară LIA activ. M-am reconectat la progresul lui.",
      );
      setNotifyOpen(true);
      window.setTimeout(() => bulkPollNowRef.current?.(), 250);
    } catch (error) {
      setBulkRepairing(false);
      setNotifySeverity("error");
      setNotifyMsg(
        `Nu am putut porni Repară LIA global: ${error.message || "eroare necunoscută"}`,
      );
      setNotifyOpen(true);
    }
  };

  const handleCopyToClipboard = (sku) => {
    const textToCopy = sku;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        console.log("SKU copied to clipboard:", textToCopy);
      })
      .catch((error) => {
        console.error("Failed to copy SKU to clipboard:", error);
      });
  };

  // --- AI Comparisons Renderer ---
  // Renders AI repair output in a readable way (fallback: pretty JSON)
  const renderAiComparisons = (ai) => {
    if (!ai) return null;

    const hasChanges = ai && Array.isArray(ai.changes) && ai.changes.length > 0;
    const hasNotes =
      ai && typeof ai.notes === "string" && ai.notes.trim().length > 0;

    const applied = ai && ai.applied ? ai.applied : null;
    const appliedList = Array.isArray(applied?.applied) ? applied.applied : [];
    const appliedOk = typeof applied?.ok === "boolean" ? applied.ok : undefined;
    const appliedStatus = applied?.woo_status;
    const appliedError = applied?.error || applied?.details;

    return (
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          borderRadius: 1,
          border: "1px solid rgba(0,0,0,0.08)",
          backgroundColor: "rgba(0,0,0,0.02)",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          AI Repair Suggestions
        </Typography>

        {hasNotes && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {ai.notes}
            </Typography>
          </Box>
        )}

        {hasChanges ? (
          <Box component="ul" sx={{ pl: 3, mb: 1 }}>
            {ai.changes.map((chg, idx) => (
              <li key={idx}>
                <Typography variant="body2">
                  {typeof chg === "string" ? chg : JSON.stringify(chg)}
                </Typography>
              </li>
            ))}
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: 12,
                background: "#f6f8fa",
                padding: 8,
                borderRadius: 8,
                maxHeight: 320,
                overflow: "auto",
                margin: 0,
              }}
            >
              {JSON.stringify(ai, null, 2)}
            </pre>
          </Box>
        )}

        {/* Applied results (Woo updates) */}
        {applied && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Applied to Woo
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              {appliedOk === true && ai.validationConfirmed !== false && (
                <Chip
                  label={`Status: OK${appliedStatus ? ` (HTTP ${appliedStatus})` : ""}`}
                  color="success"
                  size="small"
                />
              )}
              {appliedOk === true && ai.validationConfirmed === false && (
                <Chip label="Status: QA neconfirmat" color="warning" size="small" />
              )}
              {appliedOk === false && (
                <Chip label={`Status: FAILED`} color="error" size="small" />
              )}
              {appliedError && (
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {typeof appliedError === "string"
                    ? appliedError
                    : JSON.stringify(appliedError)}
                </Typography>
              )}
            </Box>
            {/* Steps from backend, if provided */}
            {Array.isArray(applied.steps) && applied.steps.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Pași executați
                </Typography>
                <Box component="ol" sx={{ pl: 3, m: 0 }}>
                  {applied.steps.map((s, i) => (
                    <li key={i}>
                      <Typography variant="body2">{String(s)}</Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
            {applied.steps && !Array.isArray(applied.steps) && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Pași executați
                </Typography>
                <Typography variant="body2">{String(applied.steps)}</Typography>
              </Box>
            )}
            {appliedList.length > 0 ? (
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {appliedList.map((it, idx) => (
                  <li key={idx}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {it.type}
                      {it.replaced &&
                        ` — replaced: "${String(it.replaced).slice(0, 64)}…"`}
                      {it.note && ` — ${it.note}`}
                      {it.error && ` — error: ${it.error}`}
                    </Typography>
                  </li>
                ))}
              </Box>
            ) : (
              <>
                {/* Fallback: show details for color synchronization if present */}
                {applied.applied && typeof applied.applied === "object" ? (
                  <Box sx={{ mt: 0.5 }}>
                    {"old" in applied.applied && (
                      <Typography variant="body2">
                        Culori vechi: {(applied.applied.old || []).join(", ")}
                      </Typography>
                    )}
                    {"new" in applied.applied && (
                      <Typography variant="body2">
                        Culori noi: {(applied.applied.new || []).join(", ")}
                      </Typography>
                    )}
                    {"updated" in applied && applied.updated === false && (
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Nu au fost necesare modificări.
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Nu există intrări aplicate sau AI nu a propus fixuri
                    aplicabile automat.
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // --- Safe full LIA repair action for one SKU ---
  const repairWithLIA = async (sku, tests) => {
    try {
      setRepairing((prev) => ({ ...prev, [sku]: true }));
      setRepairErr((prev) => ({ ...prev, [sku]: "" }));
      const outcome = await executeAutomaticLiaRepair(sku, tests || {});
      const errorText = outcome.errors.join(" | ");
      const warningText = outcome.warnings.join(" | ");

      if (outcome.tests && outcome.persisted) {
        setJsonData((prev) => {
          const updated = { ...prev, [sku]: outcome.tests };
          recalcTotals(updated);
          return updated;
        });
        setTestResults((prev) => ({ ...prev, [sku]: outcome.tests }));
      }

      setRepairErr((prev) => ({ ...prev, [sku]: errorText }));
      const resultNote = outcome.tests
        ? outcome.persisted
          ? outcome.manualTests.length
            ? `LIA a terminat. Rămân manual: ${outcome.manualTests.join(", ")}.`
            : "LIA a reparat toate testele eșuate; SKU-ul este PASSED."
          : "QA-ul final nu a putut fi salvat; produsul rămâne FAILED."
        : "LIA nu a putut confirma rezultatul final.";
      setAiRepairs((prev) => ({
        ...prev,
        [sku]: {
          notes: [
            resultNote,
            warningText ? `Avertismente: ${warningText}` : "",
            errorText ? `Erori: ${errorText}` : "",
          ]
            .filter(Boolean)
            .join("\n"),
          applied: {
            ok:
              outcome.ok && outcome.persisted
                ? outcome.manualTests.length
                  ? undefined
                  : true
                : false,
            operationOk: outcome.ok,
            persisted: outcome.persisted,
            updated: outcome.changed,
            applied: outcome.applied,
            skipped: outcome.skipped,
            warnings: outcome.warnings,
            manualTests: outcome.manualTests,
            error: errorText || undefined,
          },
        },
      }));

      setNotifySeverity(
        !outcome.persisted || !outcome.ok
          ? "warning"
          : outcome.manualTests.length
            ? "warning"
            : "success",
      );
      setNotifyMsg(
        outcome.persisted
          ? outcome.manualTests.length
            ? `Repară LIA ${sku}: au rămas ${outcome.manualTests.length} teste manuale.`
            : `Repară LIA ${sku}: toate testele sunt PASSED.`
          : `Repară LIA ${sku}: rezultatul final nu a putut fi salvat.`,
      );
      setNotifyOpen(true);
    } catch (e) {
      console.error("[repair-lia-direct] failed for", sku, e);
      setRepairErr((prev) => ({
        ...prev,
        [sku]: e.message || "Unknown error",
      }));
      setAiRepairs((prev) => ({
        ...prev,
        [sku]: { notes: `Eroare la Repară LIA: ${e.message}` },
      }));
      setNotifySeverity("error");
      setNotifyMsg(`Repară LIA: eroare pentru ${sku} - ${e.message}`);
      setNotifyOpen(true);
    } finally {
      setRepairing((prev) => ({ ...prev, [sku]: false }));
    }
  };

  // --- AI Repair only a specific test ---
  const repairTestWithLIA = async (sku, testName, tests) => {
    const key = `${sku}:${testName}`;
    try {
      setRepairingTest((prev) => ({ ...prev, [key]: true }));
      setRepairErrTest((prev) => ({ ...prev, [key]: "" }));
      setRepairStatusTest((prev) => ({ ...prev, [key]: "" }));
      const resp = await fetch(`${API_BASE}/repair-lia-direct`, {
        method: "POST",
        headers: getProductQaJobHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          sku,
          tests,
          focus_test: testName,
          retest: true,
        }),
      });
      const result = await readApiPayload(resp);

      if (!resp.ok) {
        throw new Error(getApiErrorMessage(resp, result));
      }

      const qaRetest = result.qaRetest;
      const qaRetestKey =
        qaRetest && typeof qaRetest === "object"
          ? qaRetest[sku]
            ? sku
            : Object.keys(qaRetest)[0]
          : "";
      const qaRetestSkuResult =
        qaRetestKey && qaRetest?.[qaRetestKey] ? qaRetest[qaRetestKey] : null;
      const focusedRetestResult = qaRetestSkuResult?.[testName];
      const focusedPassedAfterFix =
        focusedRetestResult === true ||
        (focusedRetestResult &&
          typeof focusedRetestResult === "object" &&
          focusedRetestResult.passed === true);
      let retestedSkuState = null;

      if (qaRetestSkuResult) {
        retestedSkuState = buildRetestedSkuState(
          jsonData[sku] || {},
          qaRetestSkuResult,
          sku,
        );
        setJsonData((prev) => {
          retestedSkuState = buildRetestedSkuState(
            prev[sku] || {},
            qaRetestSkuResult,
            sku,
          );
          const updated = { ...prev, [sku]: retestedSkuState };
          recalcTotals(updated);
          return updated;
        });
      } else if (result.updated) {
        await retestSku(sku);
      }

      if (retestedSkuState) {
        await saveTestResult(sku, retestedSkuState);
      }

      const appliedCount = Array.isArray(result.applied)
        ? result.applied.length
        : 0;
      const skippedCount = Array.isArray(result.skipped)
        ? result.skipped.length
        : 0;
      setAiRepairs((prev) => ({
        ...prev,
        [sku]: {
          notes: result.updated
            ? `LIA a aplicat ${appliedCount} corecții pentru ${testName}.`
            : `LIA nu a aplicat modificări directe pentru ${testName} (${result.reason || "nimic de reparat automat"}).`,
          applied: result,
        },
      }));
      const success = result.updated || focusedPassedAfterFix;
      setRepairStatusTest((prev) => ({
        ...prev,
        [key]: success ? "success" : "warning",
      }));
      setNotifySeverity(success ? "success" : "warning");
      setNotifyMsg(
        success
          ? `Repară LIA: ${testName} actualizat pentru ${sku}${skippedCount ? `, ${skippedCount} ignorate` : ""}.`
          : `Repară LIA: nu am găsit o corecție directă sigură pentru ${testName}.`,
      );
      setNotifyOpen(true);
    } catch (e) {
      console.error("[repair-test-with-lia] failed for", sku, testName, e);
      setRepairErrTest((prev) => ({
        ...prev,
        [key]: e.message || "Unknown error",
      }));
      setRepairStatusTest((prev) => ({ ...prev, [key]: "error" }));
      setAiRepairs((prev) => ({
        ...prev,
        [sku]: { notes: `Eroare (test ${testName}): ${e.message}` },
      }));
      setNotifySeverity("error");
      setNotifyMsg(
        `Repară LIA: eroare la ${testName} pe ${sku} - ${e.message}`,
      );
      setNotifyOpen(true);
    } finally {
      setRepairingTest((prev) => ({ ...prev, [key]: false }));
    }
  };

  // --- Dedicated repair for COLOR MATCH ---
  const repairColorMatch = async (sku, tests) => {
    const testName = "COLOR MATCH";
    const key = `${sku}:${testName}`;
    try {
      setRepairingTest((prev) => ({ ...prev, [key]: true }));
      setRepairErrTest((prev) => ({ ...prev, [key]: "" }));
      setRepairStatusTest((prev) => ({ ...prev, [key]: "" }));
      const resp = await fetch(`${API_BASE}/repair-lia-direct`, {
        method: "POST",
        headers: getProductQaJobHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          sku,
          tests,
          focus_test: testName,
          retest: true,
        }),
      });
      const result = await readApiPayload(resp);

      if (!resp.ok) {
        throw new Error(getApiErrorMessage(resp, result));
      }

      const colorRepair = Array.isArray(result.applied)
        ? result.applied.find((item) => item?.type === "COLOR_MATCH")
        : null;
      const newColors = Array.isArray(colorRepair?.new) ? colorRepair.new : [];
      setAiRepairs((prev) => ({
        ...prev,
        [sku]: {
          notes: result.updated
            ? `LIA a sincronizat culoarea din descriere (${newColors.join(", ")}).`
            : `LIA nu a aplicat modificări pentru culoare (${result.reason || "nimic de reparat automat"}).`,
          applied: result,
        },
      }));

      const qaRetest = result.qaRetest;
      const qaRetestKey =
        qaRetest && typeof qaRetest === "object"
          ? qaRetest[sku]
            ? sku
            : Object.keys(qaRetest)[0]
          : "";
      const qaRetestSkuResult =
        qaRetestKey && qaRetest?.[qaRetestKey] ? qaRetest[qaRetestKey] : null;
      const focusedRetestResult = qaRetestSkuResult?.[testName];
      const focusedPassedAfterFix =
        focusedRetestResult === true ||
        (focusedRetestResult &&
          typeof focusedRetestResult === "object" &&
          focusedRetestResult.passed === true);
      let retestedSkuState = null;
      if (qaRetestSkuResult) {
        retestedSkuState = buildRetestedSkuState(
          jsonData[sku] || {},
          qaRetestSkuResult,
          sku,
        );
        setJsonData((prev) => {
          retestedSkuState = buildRetestedSkuState(
            prev[sku] || {},
            qaRetestSkuResult,
            sku,
          );
          const updated = { ...prev, [sku]: retestedSkuState };
          recalcTotals(updated);
          return updated;
        });
      } else if (result.updated) {
        await retestSku(sku);
      }

      if (retestedSkuState) {
        await saveTestResult(sku, retestedSkuState);
      }

      const success = result.updated || focusedPassedAfterFix;
      setNotifySeverity(success ? "success" : "warning");
      setNotifyMsg(
        success
          ? result.updated
            ? `Repară LIA COLOR MATCH: actualizat la [${newColors.join(", ")}].`
            : `Repară LIA COLOR MATCH: testul trece deja după retest.`
          : `Repară LIA COLOR MATCH: nu am găsit o corecție directă sigură.`,
      );
      setNotifyOpen(true);
      setRepairStatusTest((prev) => ({
        ...prev,
        [key]: success ? "success" : "warning",
      }));
    } catch (e) {
      console.error("[repair-lia-direct COLOR MATCH] failed", sku, e);
      setRepairErrTest((prev) => ({
        ...prev,
        [key]: e.message || "Unknown error",
      }));
      setRepairStatusTest((prev) => ({ ...prev, [key]: "error" }));
      setAiRepairs((prev) => ({
        ...prev,
        [sku]: { notes: `Eroare la Repară LIA COLOR MATCH: ${e.message}` },
      }));
      setNotifySeverity("error");
      setNotifyMsg(`Repară LIA COLOR MATCH: eroare - ${e.message}`);
      setNotifyOpen(true);
    } finally {
      setRepairingTest((prev) => ({ ...prev, [key]: false }));
    }
  };

  const repairOpenAiHtmlSyntax = async (sku) => {
    const testName = "OPENAI HTML SYNTAX VALID";
    const key = `${sku}:${testName}`;
    try {
      setRepairingTest((prev) => ({ ...prev, [key]: true }));
      setRepairErrTest((prev) => ({ ...prev, [key]: "" }));
      setRepairStatusTest((prev) => ({ ...prev, [key]: "" }));
      setRepairFeedbackTest((prev) => ({ ...prev, [key]: "" }));
      const result = await requestApiPayload(`${API_BASE}/repair-openai-html`, {
        method: "POST",
        headers: getProductQaJobHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({ sku, retest: false }),
        timeoutMs: OPENAI_HTML_REPAIR_TIMEOUT_MS,
      });
      const hasAfterQa = result.afterQa !== undefined && result.afterQa !== null;
      const unchangedValidHtml =
        result.updated === false &&
        ["ALREADY_VALID", "NO_SAFE_AUTOMATIC_PATCH"].includes(result.reason) &&
        isConfirmedHtmlQa(result.beforeQa) &&
        (!hasAfterQa || isConfirmedHtmlQa(result.afterQa));
      const validatedQa = isConfirmedHtmlQa(result.afterQa)
        ? result.afterQa
        : !hasAfterQa && unchangedValidHtml
          ? result.beforeQa
          : null;
      const diagnostics = htmlRepairDiagnostics(result);

      setAiRepairs((prev) => ({
        ...prev,
        [sku]: {
          notes: result.updated
            ? `HTML reparat: ${(result.applied || []).length} patch-uri aplicate.`
            : unchangedValidHtml
              ? "HTML-ul actual este deja valid; nu este necesar un patch."
              : `HTML: nu s-a aplicat niciun patch sigur. ${diagnostics}`,
          applied: result,
          validationConfirmed: false,
        },
      }));

      const isHtmlTestPassed = (tests = {}) => {
        const value = tests?.[testName];
        return (
          !getTestKeyVariants(testName).some((variant) => tests.SKIPPED_TESTS?.includes(variant)) &&
          (value === true || isConfirmedHtmlQa(value))
        );
      };
      const buildServerValidatedSkuState = (current = {}) => {
        const updatedSku = {
          ...current,
          [testName]: {
            ...validatedQa,
            passed: true,
            issues: Array.isArray(validatedQa?.issues)
              ? validatedQa.issues
              : [],
            summary:
              validatedQa?.summary ||
              "HTML-ul actual a fost validat pe server.",
          },
        };
        updatedSku.failedTests = Array.isArray(updatedSku.failedTests)
          ? updatedSku.failedTests.filter((name) => name !== testName)
          : [];
        updatedSku.verdict = getSkuStatus(updatedSku, sku);
        return updatedSku;
      };

      let refreshedTests = null;
      let refreshWarning = "";
      let refreshQaUnconfirmed = false;
      let persistenceWarning = "";
      const shouldRefresh =
        result.updated === true || Boolean(validatedQa);

      if (shouldRefresh) {
        try {
          refreshedTests = await requestFreshSkuTests(sku, jsonData[sku] || {}, { requireHtmlResult: true });
          setJsonData((prev) => {
            const nextSku = buildRetestedSkuState(
              prev[sku] || {},
              refreshedTests,
              sku,
            );
            const updated = { ...prev, [sku]: nextSku };
            recalcTotals(updated);
            return updated;
          });
          try {
            await saveTestResult(sku, refreshedTests);
          } catch (error) {
            persistenceWarning = `rezultatul QA nu a putut fi salvat: ${
              error.message || "eroare necunoscută"
            }`;
          }
        } catch (error) {
          refreshQaUnconfirmed = error.qaUnconfirmed === true;
          refreshWarning = `retestul QA nu a putut fi confirmat: ${
            error.message || "eroare necunoscută"
          }`;
        }
      }

      const serverValidated = Boolean(validatedQa) && !refreshQaUnconfirmed;
      if (!refreshedTests && serverValidated) {
        setJsonData((prev) => {
          const updatedSku = buildServerValidatedSkuState(prev[sku] || {});
          const updated = { ...prev, [sku]: updatedSku };
          recalcTotals(updated);
          return updated;
        });
      }

      const htmlPassedAfterFix = refreshedTests
        ? isHtmlTestPassed(refreshedTests)
        : serverValidated;
      const warnings = [refreshWarning, persistenceWarning].filter(Boolean);
      const latestHtml = refreshedTests?.[testName];
      const retestDiagnostics = refreshedTests && !htmlPassedAfterFix
        ? htmlRepairDiagnostics({ afterQa: latestHtml }) ||
          (latestHtml?.skipped === true || refreshedTests.SKIPPED_TESTS?.includes(testName)
            ? "Verificarea HTML a fost omisă; rezultatul nu este confirmat."
            : "Retestul HTML nu confirmă un rezultat valid.")
        : "";
      const fullyConfirmed = htmlPassedAfterFix && warnings.length === 0;
      const baseMessage = htmlPassedAfterFix
        ? result.updated
          ? `OPENAI HTML: fix reușit pentru ${sku}; testul este PASSED.`
          : `OPENAI HTML: HTML-ul actual pentru ${sku} este deja valid; nu este necesar un patch.`
        : result.updated
          ? refreshedTests
            ? `OPENAI HTML: patch aplicat pentru ${sku}, dar retestul încă raportează probleme.`
            : `OPENAI HTML: patch aplicat pentru ${sku}, dar validarea finală nu a putut fi confirmată.`
          : refreshedTests || unchangedValidHtml
            ? `OPENAI HTML: reverificarea pentru ${sku} nu confirmă un rezultat valid.`
          : `OPENAI HTML: nu am găsit un patch automat sigur pentru ${sku}.`;

      const messageDetails = [
        ...(!fullyConfirmed ? [refreshedTests ? retestDiagnostics : diagnostics] : []),
        ...warnings,
      ].filter(Boolean);
      const feedback = messageDetails.length
        ? `${baseMessage} ${Array.from(new Set(messageDetails)).join("; ")}.`
        : baseMessage;
      setNotifySeverity(fullyConfirmed ? "success" : "warning");
      setNotifyMsg(feedback);
      setRepairFeedbackTest((prev) => ({ ...prev, [key]: feedback }));
      setNotifyOpen(true);
      setRepairStatusTest((prev) => ({
        ...prev,
        [key]: fullyConfirmed ? "success" : "warning",
      }));
      setAiRepairs((prev) => ({
        ...prev,
        [sku]: {
          ...prev[sku],
          validationConfirmed: fullyConfirmed,
          notes: refreshedTests && !htmlPassedAfterFix
            ? result.updated
              ? "Patch-urile HTML au fost aplicate, dar reverificarea raportează probleme."
              : "Descrierea a fost reverificată; rezultatul HTML este FAILED."
            : refreshQaUnconfirmed
              ? "Validarea finală HTML nu a putut fi confirmată."
              : prev[sku]?.notes,
        },
      }));
    } catch (e) {
      console.error("[repair-openai-html] failed", sku, e);
      const payload = e?.payload || {};
      const errorMessage = Array.from(
        new Set([e.message, htmlRepairDiagnostics(payload)].filter(Boolean)),
      ).join(" — ");
      setRepairErrTest((prev) => ({
        ...prev,
        [key]: errorMessage || "Unknown error",
      }));
      setRepairStatusTest((prev) => ({ ...prev, [key]: "error" }));
      setAiRepairs((prev) => ({
        ...prev,
        [sku]: { notes: `Eroare la repararea HTML: ${errorMessage}` },
      }));
      setNotifySeverity("error");
      setNotifyMsg(`OPENAI HTML: eroare la fix — ${errorMessage}`);
      setNotifyOpen(true);
    } finally {
      setRepairingTest((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSkipTest = async (sku, testName) => {
    try {
      // Persist skip in backend
      const resp = await fetch(`${API_BASE}/skip-test`, {
        method: "POST",
        headers: getProductQaJobHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({ sku, test_key: testName, reason: "manual" }),
      });
      if (!resp.ok) {
        const t = await resp.text().catch(() => "");
        throw new Error(`[skip-test ${resp.status}] ${t || resp.statusText}`);
      }

      const buildSkippedSkuResult = (current = {}) => {
        const currentValue = current[testName];
        const updatedTests = {
          ...current,
          [testName]: {
            skipped: true,
            passed: true,
            skippedOriginal: currentValue,
          },
          SKIPPED_TESTS: Array.from(
            new Set([...(current.SKIPPED_TESTS || []), testName]),
          ),
        };
        updatedTests.failedTests = Array.isArray(updatedTests.failedTests)
          ? updatedTests.failedTests.filter((name) => name !== testName)
          : [];
        updatedTests.verdict = getSkuStatus(updatedTests, sku);
        return updatedTests;
      };
      const optimisticTests = buildSkippedSkuResult(jsonData[sku] || {});

      // Reflect immediately in UI and persist TestResults
      setJsonData((prevState) => {
        const updatedTests = buildSkippedSkuResult(prevState[sku] || {});
        const updated = { ...prevState, [sku]: updatedTests };
        recalcTotals(updated);
        return updated;
      });

      setNotifySeverity("success");
      setNotifyMsg(`Testul ${testName} a fost marcat SKIPPED pentru ${sku}.`);
      setNotifyOpen(true);
      await saveTestResult(sku, optimisticTests);
    } catch (e) {
      console.error("Skip failed", e);
      setNotifySeverity("error");
      setNotifyMsg(
        `SKIPPED eșuat pentru ${sku}: ${e.message || "eroare necunoscută"}`,
      );
      setNotifyOpen(true);
    }
  };

  const handleUnskipTest = async (sku, testName) => {
    try {
      const resp = await fetch(`${API_BASE}/unskip-test`, {
        method: "POST",
        headers: getProductQaJobHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({ sku, test_key: testName }),
      });
      if (!resp.ok) {
        const t = await resp.text().catch(() => "");
        throw new Error(`[unskip-test ${resp.status}] ${t || resp.statusText}`);
      }

      setJsonData((prevState) => {
        const updatedTests = clearSkippedStateForTest(
          prevState[sku] || {},
          testName,
          sku,
        );
        const updated = { ...prevState, [sku]: updatedTests };
        recalcTotals(updated);
        return updated;
      });

      setNotifySeverity("success");
      setNotifyMsg(
        `SKIP anulat pentru ${testName} pe ${sku}. Re-rulez testele.`,
      );
      setNotifyOpen(true);

      await retestSku(sku, { unskippedTestName: testName });
    } catch (e) {
      console.error("Unskip failed", e);
      setNotifySeverity("error");
      setNotifyMsg(
        `Anulare SKIP eșuată pentru ${sku}: ${e.message || "eroare necunoscută"}`,
      );
      setNotifyOpen(true);
    }
  };

  const saveTestResult = async (sku, testCases, { signal } = {}) => {
    const verdict = getSkuStatus(testCases, sku);
    console.log("[saveTestResult] verdict for", sku, "=>", verdict);

    let lastError = null;
    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        const result = await requestApiPayload(`${API_BASE}/save-test-result`, {
          method: "POST",
          headers: getProductQaJobHeaders({
            "Content-Type": "application/json",
          }),
          body: JSON.stringify({
            sku,
            testCases,
            verdict,
          }),
          signal,
          timeoutMs: 30000,
        });
        console.log(result.message);
        return result;
      } catch (error) {
        lastError = error;
        if (signal?.aborted) throw error;
        const transient =
          !error.status || error.status === 429 || error.status >= 500;
        if (!transient || attempt === 2) throw error;
        await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
      }
    }
    throw lastError;
  };

  const isNew = (dateCreated) => {
    const currentTime = Date.now();
    const publishedTime = getPublishedTimestamp({ date_created: dateCreated });
    if (!publishedTime) return false;
    const oneDay = 24 * 60 * 60 * 1000;
    const ageMs = currentTime - publishedTime;
    return ageMs >= 0 && ageMs <= 7 * oneDay;
  };

  const getPublishedTimestamp = (tests = {}) => {
    const date = new Date(tests.date_created || "");
    const time = date.getTime();
    return Number.isNaN(time) ? 0 : time;
  };

  const formatPublishedDate = (dateCreated) => {
    if (!dateCreated) return "";
    const date = new Date(dateCreated);
    if (Number.isNaN(date.getTime())) return String(dateCreated);
    return date.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const matchesQuery = (sku, tests = {}) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;

    const haystack = [
      sku,
      tests.ID,
      tests.date_created,
      tests.slug,
      tests.permalink,
      ...Object.entries(tests)
        .filter(([key]) => !QA_METADATA_KEYS.has(key))
        .flatMap(([key, value]) => {
          if (typeof value === "boolean") {
            const failed = TRUE_MEANS_FAIL.has(key)
              ? value === true
              : value === false;
            return failed ? [key] : [];
          }
          if (typeof value === "string") return [key, value];
          if (Array.isArray(value)) return [key, value.join(" ")];
          if (value && typeof value === "object") {
            return [
              key,
              value.actual,
              value.error,
              value.expected,
              value.summary,
              value.issues,
            ]
              .filter((part) => part !== undefined && part !== null)
              .map((part) =>
                typeof part === "object" ? JSON.stringify(part) : String(part),
              );
          }
          return [key];
        }),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(needle);
  };

  const testDescriptions = {
    "SKU VALID": "Verifică dacă SKU-ul este valid.",
    "STATUS PUBLICARE VALID": "Produsul este publicat în WooCommerce.",
    "VIZIBILITATE CATALOG OK":
      "Produsul nu este ascuns din catalogul WooCommerce.",
    "TIP PRODUS SIMPLE": "Produsul este de tip WooCommerce simplu.",
    "INVENTAR ACTIV": "Verifică dacă produsul este în inventar și gestionat.",
    "PRET VALID": "Verifică dacă produsul are un preț valid.",
    "TAX STATUS & CLASS":
      "Verifică dacă Tax status este „Shipping only”, iar Tax class este „Standard”. LIA poate corecta automat ambele valori.",
    "CATEGORIE LUXURA BOUTIQUE":
      "Verifică dacă produsul aparține categoriei 'Luxura Boutique'.",
    "CONDITION MATCH":
      "Verifică dacă condiția din descrierea produsului corespunde cu atributele acestuia.",
    HAS_MIN_2_RELATED_IDS:
      "Verifică dacă produsul are cel puțin două ID-uri asociate.",
    "LAYAWAY MATCH":
      "Verifică dacă produsul poate fi achiziționat prin planuri de plată și este specificat în descriere.",
    "COLOR MATCH":
      "Verifică dacă culoarea produsului din descriere corespunde cu atributele acestuia.",
    "SORTLY ID":
      "Verifică dacă codul de inventar Sortly este prezent în descriere.",
    "MARIME INCALTAMINTE":
      "Verifică dacă mărimea încălțămintei din descriere corespunde cu slug-ul și atributele produsului.",
    "APP TEST CAT NOTDISPLAY":
      "Verifică dacă produsul nu aparține categoriei 'NOTDISPLAY'.",
    "ALL ATTRIBUTES PRESENT":
      "Verifică dacă toate atributele necesare sunt prezente.",
    "INSOTIT PRESENT":
      "Verifică dacă atributul 'Insotit' conține valorile din DETALII -> Vine însoțit.",
    "DESCRIERE VALIDA":
      "Verifică dacă există conținut în toggle-ul DESCRIERE (prag > 2999 lei).",
    "DATE IMAGINI": "Verifică dacă produsul are cel puțin o imagine.",
    "URL IMAGINI VALID": "Verifică dacă URL-urile imaginilor sunt accesibile.",
    "META DATA VALID":
      "Verifică dacă meta datele necesare sunt prezente și corect configurate.",
    "CATEGORIE PRINCIPALA VALIDA":
      "Verifică dacă produsul are o categorie principală și că aceasta este validă.",
    "TAG-URI VALIDE":
      "Verifică dacă tag-urile sunt prezente și corect configurate.",
    "BRAND TAG MATCH":
      "Tagul de brand este egal cu brandul setat în atributul Designeri.",
    "DESCRIERE NEFORMATATA":
      "Verifică dacă descrierea conține doar text fără coduri HTML neînchise sau formatări incorecte.",
    "PRET REDUS VS. PRET REGULAR":
      "Verifică dacă prețul redus este mai mic decât prețul regular.",
    "URL VALID": "Verifică dacă URL-ul produsului este valid și accesibil.",
    "DIMENSIUNI SPECIFICE":
      "Pentru SKU ce încep cu SSLEB (încălțăminte): verifică dacă mărimea din titlu corespunde cu Detalii → Mărime și cu atributul ‘Marime Incaltaminte’ (ex. 39 sau 39.5).",
    "RATING VALID": "Verifică dacă rating-ul produsului este valid.",
    "NUMAR REVIEW-URI VALID": "Verifică dacă numărul de review-uri este valid.",
    "SHORT DESCRIPTION PRESENT": "Există scurtă descriere la produs.",
    "SEO TITLE LENGTH OK": "Titlul SEO are 35–60 caractere.",
    "SEO META DESC LENGTH OK": "Meta description are 70–160 caractere.",
    "SEO TITLE CLEAN (NO HTML)": "Titlul SEO nu conține HTML.",
    "CANONICAL MATCH PERMALINK":
      "Canonical este identic cu permalink-ul produsului.",
    "OG TITLE MATCHES CLEAN NAME":
      "Titlul OpenGraph corespunde numelui produsului (fără HTML).",
    "OG DESC MIN LENGTH": "Descrierea OpenGraph are minim 20 caractere.",
    "PRIMARY IMAGE ALT FORMAT OK":
      "ALT imagine primară în formatul ‘Categorie | Brand Model’.",
    "MARIME IN ALT IMAGINI":
      "Pentru încălțăminte, dacă ALT-ul imaginilor conține o mărime, aceasta trebuie să fie aceeași cu mărimea produsului.",
    "ALL IMAGES HAVE ALT": "Toate imaginile au atribut ALT.",
    "PRODUCT SCHEMA PRESENT": "Schema JSON-LD include tipul Product.",
    "BRAND IN ALT": "Brandul se regăsește în ALT.",
    "MODEL IN ALT": "Modelul se regăsește în ALT.",
    "NAME CONTAINS HTML":
      "Informativ: numele produsului conține HTML folosit pentru layout.",
    "STRUCTURA NUME PRODUS":
      "Numele produsului păstrează structura Luxura: brand în strong și model în div.",
    "SLUG CLEAN": "Slug-ul conține doar litere, cifre și cratime.",
    "SLUG CONTAINS BRAND": "Slug-ul include brandul.",
    "PRIMARY CATEGORY IS LUXURA BOUTIQUE":
      "Prima categorie este ‘Luxura Boutique’.",
    "IMAGES COUNT MIN": "Sunt cel puțin 10 imagini per produs.",
    "OG IMAGE MATCHES PRIMARY": "Imaginea OG este aceeași cu imaginea primară.",
    "FOCUS KEYWORD SET": "Yoast focus keyword este setat.",
    "FOCUS KEYWORD MATCH NAME":
      "Focus keyword se regăsește în numele produsului.",
    "YOAST CONTENT SCORE MIN": "Yoast content score ≥ 60.",
    "KEYWORD SYNONYMS VALID":
      "Sinonimele pentru keyword (Yoast) sunt setate corect.",
    "DUPLICATE META KEYS": "Există meta chei duplicate (de evitat).",
    "HAS PREOWNED SENTENCE":
      "Secțiunea DESCRIERE conține o mențiune pre-owned / preowned / pre-loved.",
    "LAYAWAY SECTION PRESENT":
      "Secțiunea LAYAWAY este aliniată cu planul SUMO.",
    "VC STRUCTURE VALID":
      "Structura VC (row/column + toggle-uri) este completă.",
    "VC TOGGLES ORDER VALID": "Ordinea toggle-urilor este corectă.",
    "DESIGNER ID PRESENT": "Câmpul ‘Designer ID’ este completat în DETALII.",
    "ENTRUPY FIELD PRESENT": "Câmpul ‘Entrupy ID’ este completat în DETALII.",
    "SALE END DATE VALID":
      "Dacă există dată de sfârșit pentru reducere, este în viitor.",
    "PREOWNED IN CARACTERISTICI":
      "Fraza ‘Acest articol este un produs pre-owned autentic.’ apare în Caracteristici.",
    "PREOWNED IN DESCRIERE":
      "Secțiunea DESCRIERE conține o mențiune pre-owned / preowned / pre-loved.",
    "HTML TAGS BALANCED":
      "Tag-urile HTML sunt echilibrate (fără ‘</div>’ lipsă).",
    "VC SHORTCODES BALANCED":
      "Shortcode-urile VC sunt echilibrate (row/column/toggle).",
    "OPENAI HTML SYNTAX VALID":
      "OpenAI verifică sintaxa HTML/Visual Composer din descriere.",
  };

  const GROUPS = {
    CORE: [
      "SKU VALID",
      "STATUS PUBLICARE VALID",
      "VIZIBILITATE CATALOG OK",
      "TIP PRODUS SIMPLE",
      "INVENTAR ACTIV",
      "PRET VALID",
      "TAX STATUS & CLASS",
      "CATEGORIE LUXURA BOUTIQUE",
      "CONDITION MATCH",
      "COLOR MATCH",
      "SORTLY ID",
      "HAS_MIN_2_RELATED_IDS",
      "HAS MIN_2_RELATED_IDS",
      "ALL ATTRIBUTES PRESENT",
      "INSOTIT PRESENT",
      "HAS JUSTIFY IN DESCRIPTION",
      "DESCRIERE VALIDA",
      "PRET REDUS VS. PRET REGULAR",
      "DATE IMAGINI",
      "URL IMAGINI VALID",
      "META DATA VALID",
      "CATEGORIE PRINCIPALA VALIDA",
      "TAG-URI VALIDE",
      "BRAND TAG MATCH",
      "URL VALID",
      "RATING VALID",
      "NUMAR REVIEW-URI VALID",
      "MARIME INCALTAMINTE",
      "APP TEST CAT NOTDISPLAY",
      "LAYAWAY MATCH",
      "DIMENSIUNI SPECIFICE",
    ],
    SEO_MEDIA: [
      "SHORT DESCRIPTION PRESENT",
      "SEO TITLE CLEAN (NO HTML)",
      "SEO TITLE LENGTH OK",
      "SEO META DESC LENGTH OK",
      "CANONICAL MATCH PERMALINK",
      "OG TITLE MATCHES CLEAN NAME",
      "OG DESC MIN LENGTH",
      "ALL IMAGES HAVE ALT",
      "PRIMARY IMAGE ALT FORMAT OK",
      "MARIME IN ALT IMAGINI",
      "BRAND IN ALT",
      "MODEL IN ALT",
      "PRODUCT SCHEMA PRESENT",
      "OG IMAGE MATCHES PRIMARY",
      "IMAGES COUNT MIN",
      "PRIMARY IMAGE WEBP AVAILABLE",
      "IMAGE DIMENSIONS MIN",
      "INTERNAL LINKS MIN",
    ],
    EXTENDED: [
      "NAME CONTAINS HTML",
      "STRUCTURA NUME PRODUS",
      "SLUG CLEAN",
      "SLUG CONTAINS BRAND",
      "PRIMARY CATEGORY IS LUXURA BOUTIQUE",
      "FOCUS KEYWORD SET",
      "FOCUS KEYWORD MATCH NAME",
      "YOAST CONTENT SCORE MIN",
      "KEYWORD SYNONYMS VALID",
      "DUPLICATE META KEYS",
      "HAS PREOWNED SENTENCE",
      "PREOWNED IN CARACTERISTICI",
      "PREOWNED IN DESCRIERE",
      "VC STRUCTURE VALID",
      "VC TOGGLES ORDER VALID",
      "HTML TAGS BALANCED",
      "VC SHORTCODES BALANCED",
      "OPENAI HTML SYNTAX VALID",
      "DESIGNER ID PRESENT",
      "ENTRUPY FIELD PRESENT",
      "DESCRIERE NEFORMATATA",
      "SALE END DATE VALID",
    ],
  };

  const ALL_GROUP_KEYS = new Set([
    ...GROUPS.CORE,
    ...GROUPS.SEO_MEDIA,
    ...GROUPS.EXTENDED,
  ]);

  // Formatter for displaying details of "DIMENSIUNI SPECIFICE" if the backend
  // returns a structured object with extracted values.
  const formatDimSpecDetails = (obj) => {
    if (!obj || typeof obj !== "object") return "";
    if (obj.dimensions && typeof obj.dimensions === "object") {
      const labelMap = {
        length: "lungime",
        width: "latime",
        height: "inaltime",
        depth: "adancime",
      };
      const dimensionParts = Object.entries(obj.dimensions)
        .filter(
          ([, value]) => value !== undefined && value !== null && value !== "",
        )
        .map(([key, value]) => `${labelMap[key] || key}: ${value}`);
      const missing =
        Array.isArray(obj.missing) && obj.missing.length
          ? `lipsesc: ${obj.missing.join(", ")}`
          : "";
      return [...dimensionParts, missing].filter(Boolean).join(" | ");
    }
    const titleVal =
      obj.title_size ||
      obj.titleNumber ||
      obj.title ||
      obj.name_size ||
      obj.name;
    const detailsVal =
      obj.details_size ||
      obj.details ||
      obj.size_details ||
      obj.marime_detalii ||
      obj.detalii_marime;
    const attrVal =
      obj.attribute_size ||
      obj.attribute ||
      obj.marime_attr ||
      obj.marime_incaltaminte ||
      obj.MarimeIncaltaminte;

    const parts = [];
    if (titleVal !== undefined) parts.push(`titlu: ${titleVal}`);
    if (detailsVal !== undefined) parts.push(`detalii: ${detailsVal}`);
    if (attrVal !== undefined) parts.push(`atribut: ${attrVal}`);
    return parts.join(" | ");
  };

  const formatLayawayDetails = (obj) => {
    if (!obj || typeof obj !== "object") return "";
    const parts = [
      obj.enabled
        ? `SUMO: ${obj.expectedPlan || "activ fara plan"}`
        : "SUMO: inactiv",
      obj.hasLayawayBlock
        ? `descriere: ${obj.descriptionPlan || "bloc fara plan"}`
        : "descriere: fara LAYAWAY",
    ];
    if (Array.isArray(obj.reasons) && obj.reasons.length) {
      parts.push(`motive: ${obj.reasons.join(", ")}`);
    }
    return parts.join(" | ");
  };

  const formatOpenAiHtmlDetails = (obj) => {
    if (!obj || typeof obj !== "object") return "";
    const firstIssue = Array.isArray(obj.issues) ? obj.issues[0] : null;
    if (!firstIssue) return obj.summary || "";
    return [
      `${firstIssue.severity || "error"}: ${firstIssue.problem || "problema HTML"}`,
      firstIssue.location ? `loc: ${firstIssue.location}` : "",
      firstIssue.evidence ? `fragment: ${firstIssue.evidence}` : "",
    ]
      .filter(Boolean)
      .join(" | ");
  };

  // --- Styled Tabs/Tab (modern look) ---
  const StyledTabs = styled((props) => (
    <Tabs
      {...props}
      TabIndicatorProps={{
        children: <span className="MuiTabs-indicatorSpan" />,
      }}
      variant="scrollable"
      scrollButtons
      allowScrollButtonsMobile
    />
  ))(({ theme }) => ({
    "& .MuiTabs-flexContainer": {
      gap: theme.spacing(1),
    },
    "& .MuiTabs-indicator": {
      display: "flex",
      justifyContent: "center",
      backgroundColor: "transparent",
    },
    "& .MuiTabs-indicatorSpan": {
      maxWidth: 56,
      width: "100%",
      height: 4,
      borderRadius: 4,
      backgroundColor: theme.palette.mode === "dark" ? "#60a5fa" : "#2563eb",
    },
  }));

  const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
    ({ theme }) => ({
      textTransform: "none",
      fontWeight: 700,
      letterSpacing: 0.3,
      minHeight: 44,
      padding: "8px 16px",
      borderRadius: 999,
      color: theme.palette.text.secondary,
      backgroundColor:
        theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f8fafc",
      transition: "all .2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#eef2ff",
        transform: "translateY(-1px)",
      },
      "&.Mui-selected": {
        color: theme.palette.mode === "dark" ? "#e2e8f0" : "#0f172a",
        backgroundColor:
          theme.palette.mode === "dark" ? "rgba(148,163,184,0.15)" : "#eef2ff",
        boxShadow: "inset 0 0 0 2px rgba(37,99,235,0.35)",
      },
    }),
  );

  // --- Fancy Loader (mega fancy while tests run) ---
  const gradientShift = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `;

  const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;

  const floaty = keyframes`
    0%,100% { transform: translateY(0); }
    50% { transform: translateY(-12px); }
  `;

  const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  `;

  const LoaderOverlay = styled(Box)(({ theme }) => ({
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100dvh",
    minHeight: "100vh",
    zIndex: theme.zIndex.modal + 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    background: "linear-gradient(120deg, #0f172a, #1d4ed8, #0ea5e9, #0f172a)",
    backgroundSize: "300% 300%",
    animation: `${gradientShift} 10s ease infinite`,
    backdropFilter: "blur(6px)",
    overflow: "hidden",
  }));

  const LoaderContent = styled(Box)(({ theme }) => ({
    width: "min(560px, 90vw)",
    borderRadius: 16,
    padding: theme.spacing(3),
    backgroundColor: "rgba(15, 23, 42, 0.55)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "#fff",
  }));

  const Rings = styled("div")(({ theme }) => ({
    position: "relative",
    width: 140,
    height: 140,
    margin: "0 auto",
  }));

  const Ring = styled("div")(({ theme }) => ({
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: "3px solid transparent",
    borderTopColor: theme.palette.info.light,
    animation: `${spin} 1.6s linear infinite`,
    filter: "drop-shadow(0 0 10px rgba(56,189,248,0.45))",
  }));

  const Ring2 = styled(Ring)(({ theme }) => ({
    borderTopColor: theme.palette.primary.light,
    borderRightColor: theme.palette.primary.light,
    animationDuration: "2.4s",
    transform: "scale(0.82)",
    filter: "drop-shadow(0 0 10px rgba(59,130,246,0.45))",
  }));

  const Ring3 = styled(Ring)(({ theme }) => ({
    borderTopColor: theme.palette.warning.light,
    borderLeftColor: theme.palette.warning.light,
    animationDuration: "3.2s",
    transform: "scale(0.64)",
    filter: "drop-shadow(0 0 10px rgba(245,158,11,0.45))",
  }));

  const Dots = styled("div")(({ theme }) => ({
    display: "flex",
    gap: 10,
    justifyContent: "center",
    marginTop: theme.spacing(2),
  }));
  const Dot = styled("span")(({ theme }) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#fff",
    opacity: 0.8,
    animation: `${floaty} 1.6s ease-in-out infinite`,
  }));

  const ShimmerBar = styled("div")(({ theme }) => ({
    height: 10,
    borderRadius: 999,
    marginTop: theme.spacing(2),
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.08), rgba(255,255,255,0.35), rgba(255,255,255,0.08))",
    backgroundSize: "200% 100%",
    animation: `${shimmer} 1.6s linear infinite`,
  }));

  const getStatusTone = (status) => {
    if (status === "FAILED") {
      return {
        label: "Failed",
        chipColor: "error",
        icon: <CancelIcon fontSize="small" />,
        color: "#b42318",
        bg: "#fff1f0",
        softBg: "#fff7f6",
        border: "#ffb4ab",
      };
    }
    if (status === "SKIPPED") {
      return {
        label: "Skipped",
        chipColor: "warning",
        icon: <RemoveCircleIcon fontSize="small" />,
        color: "#9a6700",
        bg: "#fff8c5",
        softBg: "#fffdf0",
        border: "#f4d35e",
      };
    }
    return {
      label: "Passed",
      chipColor: "success",
      icon: <CheckCircleIcon fontSize="small" />,
      color: "#137333",
      bg: "#e8f5e9",
      softBg: "#f6fbf7",
      border: "#9ad29f",
    };
  };

  const getGroupNameForTest = (testName) => {
    if (GROUPS.CORE.includes(testName)) return "Core";
    if (GROUPS.SEO_MEDIA.includes(testName)) return "SEO & Media";
    if (GROUPS.EXTENDED.includes(testName)) return "Structură";
    return "Altele";
  };

  const getTestEvaluation = (testName, tests, sku) => {
    const shouldSkip = shouldSkipSizeTestForSku(testName, sku);
    const testStatus = shouldSkip ? "SKIPPED" : tests[testName];
    const isError =
      typeof testStatus === "string" && testStatus.startsWith("ERROR:");
    const isArray = Array.isArray(testStatus) && testStatus.length > 0;
    const isSkipped =
      shouldSkip ||
      testStatus === "SKIPPED" ||
      (testStatus &&
        typeof testStatus === "object" &&
        testStatus.skipped === true) ||
      (Array.isArray(tests.SKIPPED_TESTS) &&
        getTestKeyVariants(testName).some((variant) =>
          tests.SKIPPED_TESTS.includes(variant),
        ));
    const isInformativeOnly = IGNORED_FOR_VERDICT.has(testName);
    const isFailed =
      !isSkipped &&
      (isError ||
        isArray ||
        (!isInformativeOnly && isEntryFailed(testName, testStatus)));
    const isPassed =
      !isSkipped &&
      !isFailed &&
      (isInformativeOnly ||
        (testStatus && typeof testStatus === "object" && "passed" in testStatus
          ? testStatus.passed === true
          : TRUE_MEANS_FAIL.has(testName)
            ? testStatus === false
            : testStatus === true));
    const status = isFailed ? "FAILED" : isSkipped ? "SKIPPED" : "PASSED";

    return {
      status,
      testStatus,
      isError,
      isArray,
      isSkipped,
      isFailed,
      isPassed,
      isInformativeOnly,
    };
  };

  const getOrderedTestKeys = (tests) => {
    const knownKeys = [
      ...GROUPS.CORE,
      ...GROUPS.SEO_MEDIA,
      ...GROUPS.EXTENDED,
    ].filter((key) => key in tests);
    const otherKeys = Object.keys(tests).filter(
      (key) => !QA_METADATA_KEYS.has(key) && !ALL_GROUP_KEYS.has(key),
    );
    return Array.from(new Set([...knownKeys, ...otherKeys]));
  };

  const getRowsForSku = (tests, sku) =>
    getOrderedTestKeys(tests).map((testName) => ({
      testName,
      groupName: getGroupNameForTest(testName),
      evaluation: getTestEvaluation(testName, tests, sku),
    }));

  const getSkuSummary = (sku, tests) => {
    const rows = getRowsForSku(tests, sku);
    const status = getSkuStatus(tests, sku);
    return {
      sku,
      tests,
      status,
      rows,
      failed: rows.filter((row) => row.evaluation.status === "FAILED").length,
      skipped: rows.filter((row) => row.evaluation.status === "SKIPPED").length,
      passed: rows.filter((row) => row.evaluation.status === "PASSED").length,
    };
  };

  const formatPreviewValue = (value) => {
    if (value === undefined || value === null || value === "") return "";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value);
  };

  const getInlineDetails = (testName, testStatus) => {
    if (
      !testStatus ||
      typeof testStatus !== "object" ||
      Array.isArray(testStatus)
    ) {
      return "";
    }
    if (testName === "DIMENSIUNI SPECIFICE") {
      return formatDimSpecDetails(testStatus);
    }
    if (
      testName === "LAYAWAY MATCH" ||
      testName === "LAYAWAY SECTION PRESENT"
    ) {
      return formatLayawayDetails(testStatus);
    }
    if (testName === "OPENAI HTML SYNTAX VALID") {
      return formatOpenAiHtmlDetails(testStatus);
    }
    if (testStatus.summary) return String(testStatus.summary);
    if (testStatus.error) return String(testStatus.error);
    return "";
  };

  const renderValueBlock = (label, value, color = "text.secondary") => {
    const preview = formatPreviewValue(value);
    if (!preview) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {label}
        </Typography>
        <Box
          component="pre"
          sx={{
            mt: 0.4,
            mb: 0,
            p: 1,
            maxHeight: 220,
            overflow: "auto",
            borderRadius: 1,
            border: "1px solid rgba(15,23,42,0.08)",
            backgroundColor: "rgba(255,255,255,0.72)",
            color: "text.primary",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
            fontSize: 12,
          }}
        >
          {preview}
        </Box>
      </Box>
    );
  };

  const renderTestCard = (row, tests, sku) => {
    const { testName, groupName, evaluation } = row;
    const { testStatus, status, isFailed, isSkipped, isError, isArray } =
      evaluation;
    const tone = getStatusTone(status);
    const details = getInlineDetails(testName, testStatus);
    const repairKey = `${sku}:${testName}`;
    const isRepairing = !!repairingTest[repairKey];
    const repairError = repairErrTest[repairKey];
    const repairStatus = repairStatusTest[repairKey];
    const repairFeedback = repairFeedbackTest[repairKey];
    const actionIconSx = {
      width: 34,
      height: 34,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "white",
      boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
      "&:hover": {
        backgroundColor: "#f8fafc",
      },
    };
    const repairIconColor =
      repairStatus === "success"
        ? "success.main"
        : repairStatus === "error"
          ? "error.main"
          : repairStatus === "warning"
            ? "warning.main"
            : "secondary.main";
    const canSkip = isFailed && !isSkipped && !isError && !isArray;
    const canRunGenericRepair =
      isFailed &&
      !isSkipped &&
      !isError &&
      LIA_DIRECT_REPAIR_TESTS.has(testName) &&
      testName !== "COLOR MATCH" &&
      testName !== "OPENAI HTML SYNTAX VALID";

    return (
      <Box
        key={testName}
        sx={{
          p: 1.5,
          borderRadius: 1,
          border: "1px solid",
          borderColor: tone.border,
          borderLeft: `5px solid ${tone.color}`,
          backgroundColor: tone.softBg,
          boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: tone.color,
                  backgroundColor: tone.bg,
                }}
              >
                {tone.icon}
              </Box>
              <Tooltip title={testDescriptions[testName] || ""} arrow>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 900,
                    color: "#111827",
                    lineHeight: 1.25,
                    overflowWrap: "anywhere",
                  }}
                >
                  {testName}
                </Typography>
              </Tooltip>
              <Chip
                size="small"
                label={tone.label}
                color={tone.chipColor}
                sx={{ height: 22, fontWeight: 800 }}
              />
              <Chip
                size="small"
                label={groupName}
                variant="outlined"
                sx={{ height: 22, fontWeight: 700, backgroundColor: "white" }}
              />
              {evaluation.isInformativeOnly && (
                <Chip
                  size="small"
                  label="Info"
                  variant="outlined"
                  sx={{ height: 22, fontWeight: 700 }}
                />
              )}
              {repairError && (
                <Tooltip title={repairError}>
                  <Chip label="Eroare fix" color="error" size="small" />
                </Tooltip>
              )}
            </Box>
            {(testDescriptions[testName] || details) && (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.7,
                  color: "#4b5563",
                  lineHeight: 1.45,
                  overflowWrap: "anywhere",
                }}
              >
                {details || testDescriptions[testName]}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.75,
              flexWrap: "wrap",
              minWidth: { xs: "auto", md: 210 },
            }}
          >
            {canSkip && (
              <Tooltip title="Marchează ca SKIPPED">
                <span>
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => handleSkipTest(sku, testName)}
                    disabled={bulkRepairing}
                    aria-label={`Marchează ca SKIPPED ${testName}`}
                    sx={actionIconSx}
                  >
                    <RemoveCircleIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {isSkipped && (
              <Tooltip title="Anulează SKIP">
                <span>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => handleUnskipTest(sku, testName)}
                    disabled={bulkRepairing}
                    aria-label={`Anulează SKIP ${testName}`}
                    sx={actionIconSx}
                  >
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {isFailed && testName === "COLOR MATCH" && (
              <Tooltip title="Repară LIA COLOR MATCH">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => repairColorMatch(sku, tests)}
                    disabled={bulkRepairing || isRepairing}
                    aria-label={`Repară LIA COLOR MATCH pentru ${sku}`}
                    sx={{ ...actionIconSx, color: repairIconColor }}
                  >
                    {isRepairing ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AutoFixHighIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {isFailed && testName === "OPENAI HTML SYNTAX VALID" && (
              <Tooltip title="Fix HTML cu LIA">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => repairOpenAiHtmlSyntax(sku)}
                    disabled={bulkRepairing || isRepairing}
                    aria-label={`Fix HTML pentru ${sku}`}
                    sx={{ ...actionIconSx, color: repairIconColor }}
                  >
                    {isRepairing ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AutoFixHighIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canRunGenericRepair && (
              <Tooltip title="Repară cu LIA">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => repairTestWithLIA(sku, testName, tests)}
                    disabled={bulkRepairing || isRepairing}
                    aria-label={`Repară cu LIA ${testName}`}
                    sx={{ ...actionIconSx, color: repairIconColor }}
                  >
                    {isRepairing ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AutoFixHighIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {repairStatus === "success" && !isRepairing && (
              <Tooltip title="Reparare finalizată">
                <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
              </Tooltip>
            )}
            {repairStatus === "error" && !isRepairing && (
              <Tooltip title={repairError || "Reparare eșuată"}>
                <CancelIcon sx={{ color: "error.main", fontSize: 20 }} />
              </Tooltip>
            )}
            {repairStatus === "warning" && !isRepairing && (
              <Tooltip title={repairFeedback || "LIA nu a găsit un fix automat sigur"}>
                <RemoveCircleIcon
                  sx={{ color: "warning.main", fontSize: 20 }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        {repairStatus === "warning" && repairFeedback && !isRepairing && (
          <Typography variant="body2" sx={{ mt: 1, color: "warning.dark", overflowWrap: "anywhere" }}>
            {repairFeedback}
          </Typography>
        )}

        {isError && renderValueBlock("Eroare", testStatus, "warning.main")}
        {isArray && renderValueBlock("Detalii", testStatus, "secondary.main")}
        {isFailed &&
          testStatus &&
          typeof testStatus === "object" &&
          !Array.isArray(testStatus) &&
          renderValueBlock("Valoare găsită", testStatus.actual, "error.main")}
        {isFailed &&
          testStatus &&
          typeof testStatus === "object" &&
          !Array.isArray(testStatus) &&
          testStatus.error &&
          renderValueBlock("Mesaj", testStatus.error, "warning.main")}
      </Box>
    );
  };

  const renderTestSection = (title, rows, status, tests, sku) => {
    const tone = getStatusTone(status);
    return (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            mb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ color: tone.color, display: "inline-flex" }}>
              {tone.icon}
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              {title}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={rows.length}
            color={tone.chipColor}
            sx={{ fontWeight: 900 }}
          />
        </Box>
        {rows.length ? (
          <Box sx={{ display: "grid", gap: 1 }}>
            {rows.map((row) => renderTestCard(row, tests, sku))}
          </Box>
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: "1px dashed rgba(15,23,42,0.18)",
              color: "text.secondary",
              backgroundColor: "rgba(255,255,255,0.64)",
            }}
          >
            <Typography variant="body2">
              Niciun test în această secțiune.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const statusSortWeight = { FAILED: 0, PASSED: 1 };
  const skuSummaries = Object.keys(jsonData)
    .map((sku) => getSkuSummary(sku, jsonData[sku] || {}))
    .sort((a, b) => {
      const byPublishedDate =
        getPublishedTimestamp(b.tests) - getPublishedTimestamp(a.tests);
      if (byPublishedDate !== 0) return byPublishedDate;
      const byStatus =
        (statusSortWeight[a.status] ?? 99) - (statusSortWeight[b.status] ?? 99);
      if (byStatus !== 0) return byStatus;
      if (b.failed !== a.failed) return b.failed - a.failed;
      return a.sku.localeCompare(b.sku);
    });
  const filteredSkuSummaries = skuSummaries.filter(
    (summary) =>
      matchesQuery(summary.sku, summary.tests) &&
      (activeTab === "all" || summary.status.toLowerCase() === activeTab),
  );
  const exactSkuQuery = isExactSkuQuery(query) ? normalizeSkuQuery(query) : "";
  const exactSkuSummary = exactSkuQuery
    ? skuSummaries.find(
        (summary) => normalizeSkuQuery(summary.sku) === exactSkuQuery,
      )
    : null;
  const visibleSkuKeys = filteredSkuSummaries.map((summary) => summary.sku);
  const visibleSkuKey = visibleSkuKeys.join("|");
  const firstVisibleSku = visibleSkuKeys[0] || "";
  const selectedSkuIsVisible = visibleSkuKeys.includes(selectedSku);
  const selectedSummary =
    filteredSkuSummaries.find((summary) => summary.sku === selectedSku) ||
    filteredSkuSummaries[0] ||
    null;
  const selectedTests = selectedSummary?.tests || null;
  const selectedTone = selectedSummary
    ? getStatusTone(selectedSummary.status)
    : getStatusTone("PASSED");
  const selectedRows = selectedSummary?.rows || [];
  const failedRows = selectedRows.filter(
    (row) => row.evaluation.status === "FAILED",
  );
  const skippedRows = selectedRows.filter(
    (row) => row.evaluation.status === "SKIPPED",
  );
  const passedRows = selectedRows.filter(
    (row) => row.evaluation.status === "PASSED",
  );

  useEffect(() => {
    if (!firstVisibleSku) {
      if (selectedSku) setSelectedSku("");
      return;
    }
    if (!selectedSku || !selectedSkuIsVisible) {
      setSelectedSku(firstVisibleSku);
    }
  }, [firstVisibleSku, selectedSku, selectedSkuIsVisible, visibleSkuKey]);

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        maxWidth: 1680,
        mx: "auto",
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f8fafc 0%, #ffffff 44%, #fff7ed 100%)",
      }}
    >
      <Snackbar
        open={notifyOpen}
        autoHideDuration={3500}
        onClose={() => setNotifyOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setNotifyOpen(false)}
          severity={notifySeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notifyMsg}
        </Alert>
      </Snackbar>

      {loading && (
        <Portal>
          <LoaderOverlay>
            <LoaderContent>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 2, opacity: 0.8 }}
              >
                Rulez testele…
              </Typography>
              <Rings>
                <Ring />
                <Ring2 />
                <Ring3 />
              </Rings>
              <Dots>
                <Dot style={{ animationDelay: "0ms" }} />
                <Dot style={{ animationDelay: "120ms" }} />
                <Dot style={{ animationDelay: "240ms" }} />
                <Dot style={{ animationDelay: "360ms" }} />
              </Dots>
              <ShimmerBar />
              <Typography
                variant="body2"
                sx={{ mt: 1.5, opacity: 0.85, textAlign: "center" }}
              >
                Optimizăm SEO, verificăm imagini și structură…
              </Typography>
            </LoaderContent>
          </LoaderOverlay>
        </Portal>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: "1px solid rgba(15,23,42,0.08)",
          boxShadow: "0 18px 48px rgba(15,23,42,0.08)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.96), rgba(248,250,252,0.94))",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 240 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 950, color: "#111827", letterSpacing: 0 }}
            >
              Product QA Dashboard
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.4, color: "#64748b" }}>
              {filteredSkuSummaries.length} SKU-uri afișate din {totalSkus}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Chip label={`Failed ${failedCount}`} color="error" />
            <Chip label={`Passed ${passedCount}`} color="success" />
            <Tooltip title="Rulează testele pentru toate SKU-urile">
              <span>
                <Button
                  variant="contained"
                  onClick={runTests}
                  disabled={
                    bulkRepairing || loading || hasSingleQaActionInProgress
                  }
                  sx={{ fontWeight: 800 }}
                >
                  Rulează testele
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Pornește pe server repararea tuturor SKU-urilor FAILED. Poți părăsi pagina și primești raport pe email.">
              <span>
                <Button
                  variant="outlined"
                  onClick={repairAllFailedWithLIA}
                  disabled={
                    bulkRepairing ||
                    loading ||
                    hasSingleQaActionInProgress ||
                    failedCount === 0
                  }
                  startIcon={
                    bulkRepairing ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <AutoFixHighIcon />
                    )
                  }
                  sx={{ fontWeight: 800, backgroundColor: "white" }}
                >
                  {bulkRepairing
                    ? `LIA în fundal ${bulkProgress.completed}/${bulkProgress.total}`
                    : "Repară LIA"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <Box
            sx={{
              height: 8,
              display: "flex",
              overflow: "hidden",
              borderRadius: 999,
              backgroundColor: "rgba(15,23,42,0.08)",
            }}
          >
            <Box
              sx={{
                width: totalSkus ? `${(failedCount / totalSkus) * 100}%` : 0,
                backgroundColor: "error.main",
              }}
            />
            <Box
              sx={{
                width: totalSkus ? `${(passedCount / totalSkus) * 100}%` : 0,
                backgroundColor: "success.main",
              }}
            />
          </Box>
          {bulkProgress.total > 0 && (
            <Box sx={{ mt: 1.2 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  100,
                  (bulkProgress.completed / bulkProgress.total) * 100,
                )}
                color={bulkProgress.errors ? "warning" : "secondary"}
                sx={{ height: 7, borderRadius: 999 }}
              />
              <Typography variant="body2" sx={{ mt: 0.8, color: "#64748b" }}>
                {bulkRepairing
                  ? `LIA: ${bulkProgress.completed} din ${bulkProgress.total} finalizate${bulkProgress.sku ? ` — acum ${bulkProgress.sku}` : ""}`
                  : bulkJob?.status === "FAILED"
                    ? `LIA oprit cu eroare: ${bulkProgress.completed} din ${bulkProgress.total}`
                    : `LIA finalizat: ${bulkProgress.completed} din ${bulkProgress.total}`}
                {` · Modificate ${bulkProgress.changed} · PASSED ${bulkProgress.passed} · Manual ${bulkProgress.manual} · Erori ${bulkProgress.errors}`}
                {bulkProgress.manualSkus.length
                  ? ` · SKU manual: ${bulkProgress.manualSkus.slice(0, 5).join(", ")}${bulkProgress.manualSkus.length > 5 ? ", …" : ""}`
                  : ""}
                {bulkProgress.errorSkus.length
                  ? ` · SKU erori: ${bulkProgress.errorSkus.slice(0, 5).join(", ")}${bulkProgress.errorSkus.length > 5 ? ", …" : ""}`
                  : ""}
                {bulkRepairing
                  ? " · Rulează pe server; poți părăsi pagina. Primești email la final."
                  : bulkJob?.emailStatus === "SENT"
                    ? " · Raport trimis pe suport@luxuraelite.ro."
                    : bulkJob?.emailStatus === "FAILED"
                      ? " · Raportul email nu a putut fi trimis."
                      : ""}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "360px minmax(0, 1fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid rgba(15,23,42,0.08)",
            overflow: "hidden",
            position: { lg: "sticky" },
            top: { lg: 12 },
            boxShadow: "0 16px 36px rgba(15,23,42,0.08)",
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: "1px solid rgba(15,23,42,0.08)" }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Caută SKU, test, ID, slug…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ opacity: 0.72 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.2 }}
            />
            <StyledTabs value={activeTab} onChange={handleTabChange}>
              <StyledTab value="all" label="Toate" />
              <StyledTab
                value="failed"
                icon={<CancelIcon fontSize="small" />}
                iconPosition="start"
                label="Failed"
              />
              <StyledTab
                value="passed"
                icon={<CheckCircleIcon fontSize="small" />}
                iconPosition="start"
                label="Passed"
              />
            </StyledTabs>
          </Box>

          <Box
            sx={{
              maxHeight: { xs: 360, lg: "calc(100vh - 260px)" },
              overflow: "auto",
              p: 1,
              backgroundColor: "#f8fafc",
            }}
          >
            {filteredSkuSummaries.length ? (
              filteredSkuSummaries.map((summary) => {
                const tone = getStatusTone(summary.status);
                const active = selectedSummary?.sku === summary.sku;
                return (
                  <Box
                    key={summary.sku}
                    component="button"
                    type="button"
                    onClick={() => setSelectedSku(summary.sku)}
                    sx={{
                      width: "100%",
                      mb: 0.8,
                      p: 1.2,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: active ? tone.border : "rgba(15,23,42,0.08)",
                      borderLeft: `5px solid ${tone.color}`,
                      backgroundColor: active ? tone.softBg : "white",
                      color: "inherit",
                      cursor: "pointer",
                      textAlign: "left",
                      boxShadow: active
                        ? "0 12px 28px rgba(15,23,42,0.12)"
                        : "0 6px 16px rgba(15,23,42,0.04)",
                      transition: "transform .15s ease, box-shadow .15s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 14px 30px rgba(15,23,42,0.10)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 950,
                          color: "#111827",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {summary.sku}
                      </Typography>
                      <Chip
                        size="small"
                        label={tone.label}
                        color={tone.chipColor}
                        sx={{ height: 22, fontWeight: 800 }}
                      />
                    </Box>
                    <Box
                      sx={{
                        mt: 0.8,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        flexWrap: "wrap",
                      }}
                    >
                      {isNew(summary.tests.date_created) && (
                        <Chip
                          size="small"
                          icon={
                            <CalendarTodayIcon
                              sx={{
                                color: "#ffffff !important",
                                fontSize: "14px !important",
                              }}
                            />
                          }
                          label="Nou"
                          sx={{
                            height: 22,
                            px: 0.35,
                            color: "#ffffff",
                            fontWeight: 950,
                            background:
                              "linear-gradient(135deg, #ec4899 0%, #f97316 100%)",
                            boxShadow: "0 8px 18px rgba(236,72,153,0.22)",
                            border: "1px solid rgba(255,255,255,0.65)",
                            "& .MuiChip-label": { px: 0.7 },
                          }}
                        />
                      )}
                      {summary.tests.ID && (
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          ID {summary.tests.ID}
                        </Typography>
                      )}
                      {summary.tests.date_created && (
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          Publicat{" "}
                          {formatPublishedDate(summary.tests.date_created)}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 0.6,
                        display: "block",
                        color: "#64748b",
                        fontWeight: 700,
                      }}
                    >
                      {summary.failed} failed · {summary.skipped} skipped ·{" "}
                      {summary.passed} passed
                    </Typography>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
                <Typography variant="body2">
                  Nu există SKU-uri pentru filtrul curent.
                </Typography>
                {exactSkuQuery && (
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    disabled={
                      bulkRepairing || searchingSku || retesting[exactSkuQuery]
                    }
                    onClick={() => {
                      if (exactSkuSummary) {
                        setActiveTab(exactSkuSummary.status.toLowerCase());
                        setSelectedSku(exactSkuSummary.sku);
                        return;
                      }
                      loadSearchedSku(exactSkuQuery);
                    }}
                    startIcon={
                      searchingSku || retesting[exactSkuQuery] ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <SearchIcon fontSize="small" />
                      )
                    }
                    sx={{ mt: 1.2, borderRadius: 1, fontWeight: 850 }}
                  >
                    {exactSkuSummary ? "Deschide SKU-ul" : "Rulează SKU-ul"}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Paper>

        <Box sx={{ minWidth: 0 }}>
          {selectedSummary && selectedTests ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(15,23,42,0.08)",
                overflow: "hidden",
                boxShadow: "0 16px 36px rgba(15,23,42,0.08)",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(15,23,42,0.08)",
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.94))",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 950,
                          color: "#111827",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {selectedSummary.sku}
                      </Typography>
                      <Chip
                        icon={selectedTone.icon}
                        label={selectedTone.label}
                        color={selectedTone.chipColor}
                        sx={{ fontWeight: 900 }}
                      />
                      {isAiRepairConfirmed(aiRepairs[selectedSummary.sku]) && (
                        <Chip
                          label={`AI fixed${aiRepairs[selectedSummary.sku]?.applied?.woo_status ? ` HTTP ${aiRepairs[selectedSummary.sku]?.applied?.woo_status}` : ""}`}
                          color="success"
                          size="small"
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        color: "#64748b",
                      }}
                    >
                      {selectedTests.date_created && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <CalendarTodayIcon sx={{ fontSize: 17 }} />
                          <Typography variant="body2">
                            {selectedTests.date_created}
                          </Typography>
                        </Box>
                      )}
                      {selectedTests.ID && (
                        <Typography variant="body2">
                          ID {selectedTests.ID}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        {failedRows.length} failed · {skippedRows.length}{" "}
                        skipped · {passedRows.length} passed
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 0.8,
                      flexWrap: "wrap",
                    }}
                  >
                    <Tooltip title="Rulează din nou testele doar pentru acest SKU">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => retestSku(selectedSummary.sku)}
                          disabled={
                            bulkRepairing || !!retesting[selectedSummary.sku]
                          }
                          aria-label={`Rulează din nou testele pentru ${selectedSummary.sku}`}
                          sx={{
                            width: 36,
                            height: 36,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "white",
                          }}
                        >
                          {retesting[selectedSummary.sku] ? (
                            <CircularProgress size={16} />
                          ) : (
                            <ReplayIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Copiază SKU">
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleCopyToClipboard(selectedSummary.sku)
                        }
                        sx={{
                          width: 36,
                          height: 36,
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "white",
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {selectedTests.ID && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          href={
                            selectedTests.permalink ||
                            `https://luxuraelite.ro/produs/${encodeURIComponent(
                              selectedTests.slug || "",
                            )}`
                          }
                          target="_blank"
                          rel="noopener"
                          sx={{ backgroundColor: "white", fontWeight: 800 }}
                        >
                          Vezi produs
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          href={`https://luxuraelite.ro/wp-admin/post.php?post=${selectedTests.ID}&action=edit`}
                          disabled={bulkRepairing}
                          target="_blank"
                          rel="noopener"
                          sx={{ backgroundColor: "white", fontWeight: 800 }}
                        >
                          Editează WP
                        </Button>
                      </>
                    )}
                    {selectedSummary.status === "FAILED" && (
                      <>
                        <Tooltip title="Repară LIA">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                repairWithLIA(
                                  selectedSummary.sku,
                                  selectedTests,
                                )
                              }
                              disabled={
                                bulkRepairing ||
                                !!repairing[selectedSummary.sku]
                              }
                              aria-label={`Repară LIA ${selectedSummary.sku}`}
                              sx={{
                                width: 36,
                                height: 36,
                                border: "1px solid",
                                borderColor: "divider",
                                backgroundColor: "white",
                                color: repairErr[selectedSummary.sku]
                                  ? "error.main"
                                  : isAiRepairConfirmed(aiRepairs[selectedSummary.sku])
                                    ? "success.main"
                                    : "secondary.main",
                              }}
                            >
                              {repairing[selectedSummary.sku] ? (
                                <CircularProgress size={16} />
                              ) : (
                                <AutoFixHighIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {isAiRepairConfirmed(aiRepairs[selectedSummary.sku]) &&
                          !repairing[selectedSummary.sku] && (
                            <Tooltip title="Reparare finalizată">
                              <CheckCircleIcon
                                sx={{ color: "success.main", fontSize: 20 }}
                              />
                            </Tooltip>
                          )}
                        {repairErr[selectedSummary.sku] && (
                          <Tooltip title={repairErr[selectedSummary.sku]}>
                            <CancelIcon
                              sx={{ color: "error.main", fontSize: 20 }}
                            />
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 2, backgroundColor: "#f8fafc" }}>
                {renderTestSection(
                  "Failed",
                  failedRows,
                  "FAILED",
                  selectedTests,
                  selectedSummary.sku,
                )}
                {renderTestSection(
                  "Skipped",
                  skippedRows,
                  "SKIPPED",
                  selectedTests,
                  selectedSummary.sku,
                )}
                {renderTestSection(
                  "Passed",
                  passedRows,
                  "PASSED",
                  selectedTests,
                  selectedSummary.sku,
                )}
                {aiRepairs[selectedSummary.sku] &&
                  renderAiComparisons(aiRepairs[selectedSummary.sku])}
              </Box>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 2,
                border: "1px solid rgba(15,23,42,0.08)",
                textAlign: "center",
                color: "text.secondary",
                boxShadow: "0 16px 36px rgba(15,23,42,0.08)",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 900, color: "#111827" }}
              >
                Nu există niciun SKU selectat
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Rulează testele sau schimbă filtrul din sidebar.
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Page4;
